import { oneDark } from "@codemirror/theme-one-dark";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Database,
  Edit,
  File,
  FileCode,
  FileText,
  Folder,
  Loader2,
  MessageCircle,
  Moon,
  PanelBottomClose,
  PanelBottomOpen,
  Play,
  Plus,
  RotateCcw,
  Save,
  Search,
  Share2,
  Square,
  Sun,
  Trash2,
  Upload,
} from "lucide-react";
import React, {
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigation } from "../App";
import AIAssistantChat from "../components/AIAssistantChat";
import ProjectMessagesChat from "../components/ProjectMessagesChat";
import { AutoSizeInput } from "../components/ui/AutoSizeInput";
import { cn } from "../lib/utils";
import paths from "../routes";
import "../styles/sandbox-dark.css";
//import useNavigation from "../components/Context/NavigationContext";
import { python } from "@codemirror/lang-python";
import { EditorView } from "@codemirror/view";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { githubLight } from "@uiw/codemirror-theme-github";
import CodeMirror from "@uiw/react-codemirror";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  createSession,
  getContents,
  getKernel,
  isDatasetPointerContent,
  listSessions,
  useDeleteContentMutation,
  useExecuteCellMutation,
  useExecuteNotebookMutation,
  useInterruptKernelMutation,
  useRenameContentMutation,
  useRestartKernelMutation,
  useUpdateContentMutation,
  useUploadJupyterFileMutation,
} from "../api/jupyterManagementApi";
import { useAuth } from "../App";
import CSVEditor from "../components/CSVEditor";
import ExcelEditor from "../components/ExcelEditor";
import FileSharing from "../components/FileSharing";
import MarkdownCell from "../components/MarkdownCell";
import SubmitAssignmentDialog from "../components/SubmitAssignmentDialog";
import Timer from "../components/Timer";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ROLES } from "../constant/roles";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";

// Custom selection highlight - orange color
const selectionHighlight = EditorView.theme({
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
    {
      backgroundColor: "#ffa500 !important",
    },

  ".cm-selectionMatch": {
    backgroundColor: "#ffe0b3 !important",
  },
});

interface Cell {
  id: string;
  type: "code" | "markdown";
  content: string;
  output?: any[];
  execution_count?: number | null;
  metadata: any;
  ui?: {
    showOutput: boolean;
  };
}

interface NotebookContentData {
  cells: Cell[];
  metadata: any;
  nbformat: number;
  nbformat_minor: number;
}

interface NotebookFile extends FileItem {
  type: "notebook";
  content: NotebookContentData;
}

interface Notebook {
  name: string;
  path: string;
  content: NotebookContentData;
}

interface FileItem {
  name: string;
  path: string;
  type: "file" | "directory" | "notebook";
  size?: number;
  last_modified?: string;
  mimetype?: string;
  content?: any;
}

function fileItemIsNotebook(file: FileItem | null | undefined): boolean {
  if (!file?.name) return false;
  return (
    file.type === "notebook" ||
    file.name.endsWith(".ipynb") ||
    file.mimetype === "application/x-ipynb+json"
  );
}

function cloneNotebookCellsForDraft(cells: Cell[]): Cell[] {
  return cells.map((c) => ({
    ...c,
    output: Array.isArray(c.output) ? [...c.output] : c.output ?? [],
    metadata: c.metadata ? { ...c.metadata } : {},
    ui: c.ui ? { ...c.ui } : { showOutput: true },
  }));
}

function mapFetchedNotebookCells(apiCells: any[]): Cell[] {
  const transformedCells = apiCells.map((apiCell: any) => ({
    id:
      apiCell.id ||
      `cell_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    type: apiCell.cell_type,
    content: Array.isArray(apiCell.source)
      ? apiCell.source.join("\n")
      : apiCell.source || "",
    output: apiCell.outputs || [],
    execution_count: apiCell.execution_count ?? null,
    metadata: apiCell.metadata || {},
    ui: { showOutput: true },
  }));
  if (transformedCells.length === 0) {
    transformedCells.push({
      id: `cell_${Date.now()}`,
      type: "code",
      content: "",
      output: [],
      execution_count: null,
      metadata: {},
      ui: { showOutput: true },
    });
  }
  return transformedCells;
}

// --- HELPER FUNCTIONS ---
const getFileIcon = (file: FileItem) => {
  if (file.type === "directory") {
    return <Folder size={16} className="text-yellow-500" />;
  }
  const ext = file?.name?.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "ipynb":
      return <FileText size={16} className="text-orange-400" />;
    case "py":
      return <FileCode size={16} className="text-green-500" />;
    case "csv":
    case "json":
    case "txt":
    case "md":
    case "xlsx":
    case "xls":
      return <FileText size={16} className="text-blue-400" />;
    default:
      return <File size={16} className="text-gray-400" />;
  }
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const EDITABLE_EXTENSIONS = [
  "py",
  "r",
  "jl",
  "scala",
  "java",
  "c",
  "cpp",
  "h",
  "m",
  "sh",
  "js",
  "ts",
  "php",
  "rb",
  "pl",
  "txt",
  "md",
  "rst",
  "json",
  "yaml",
  "yml",
  "xml",
];

// File upload configuration for LTI endpoint
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const SUPPORTED_FILE_TYPES = [
  "ipynb",
  "py",
  "csv",
  "xlsx",
  "xls",
  "txt",
  "md",
  "markdown",
];

const isCsvFile = (file: FileItem | null): boolean => {
  if (!file?.name) return false;
  const extension = file?.name?.split(".").pop()?.toLowerCase();
  return extension === "csv";
};

const isXlFile = (file: FileItem | null): boolean => {
  if (!file?.name) return false;
  const extension = file?.name?.split(".").pop()?.toLowerCase();
  return extension === "xls" || extension === "xlsx";
};

const isEditableFile = (file: FileItem | null): boolean => {
  if (!file?.name) return false;
  const extension = file?.name?.split(".").pop()?.toLowerCase();
  return !!extension && EDITABLE_EXTENSIONS.includes(extension);
};

const formatTimeAgo = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  let interval = seconds / 86400;
  if (interval > 1) return `${Math.floor(interval)}d ago`;
  interval = seconds / 3600;
  if (interval > 1) return `${Math.floor(interval)}h ago`;
  interval = seconds / 60;
  if (interval > 1) return `${Math.floor(interval)}m ago`;
  return `${Math.floor(seconds)}s ago`;
};

function getUniqueFilename(
  existingNames: string[],
  baseName = "Untitled",
  ext = ".ipynb",
) {
  const existingSet = new Set(existingNames);
  let i = 0;
  let candidate = `${baseName}${ext}`;

  if (!existingSet.has(candidate)) return candidate;

  i = 1;
  while (true) {
    candidate = `${baseName}${i}${ext}`;
    if (!existingSet.has(candidate)) return candidate;
    i++;
  }
}

const sortFiles = (a: FileItem, b: FileItem) => {
  // 1. Directories first
  if (a.type === "directory" && b.type !== "directory") return -1;
  if (a.type !== "directory" && b.type === "directory") return 1;

  // 2. Then by recent
  const ta = a.last_modified ? new Date(a.last_modified).getTime() : 0;
  const tb = b.last_modified ? new Date(b.last_modified).getTime() : 0;
  return tb - ta;
};

/** Path relative to the notebook kernel cwd — strip known workspace prefix only (not the first segment blindly). */
function pathRelativeToNotebookRoot(
  fullPath: string,
  folderPath: string | null | undefined,
  workspaceType: "cloud" | "local",
  projectId: string | null | undefined,
): string {
  const p = fullPath.trim().replace(/^\/+/, "");
  if (!p) return "";

  const tryStrip = (prefixRaw: string | null | undefined): string | null => {
    const prefix = (prefixRaw || "").replace(/^\/+|\/+$/g, "");
    if (!prefix) return null;
    if (p === prefix) return "";
    if (p.startsWith(`${prefix}/`)) return p.slice(prefix.length + 1);
    return null;
  };

  const fromFolder = tryStrip(folderPath);
  if (fromFolder !== null) return fromFolder;

  if (workspaceType === "cloud" && projectId) {
    const fromProject = tryStrip(projectId);
    if (fromProject !== null) return fromProject;
  }

  return p;
}

const normalizeJupyterContentPath = (p: string) =>
  p.trim().replace(/^\/+/, "").replace(/\/+$/, "");

/**
 * Find or create a Jupyter kernel for the given notebook when workspace.kernelId is stale or missing.
 * Passes projectId to listSessions so the gateway scopes correctly (local agent / cloud).
 */
async function resolveJupyterKernelForExecution(opts: {
  projectId: string;
  notebookPath: string;
  currentKernelId?: string | null;
}): Promise<string | null> {
  const { projectId, notebookPath, currentKernelId } = opts;

  if (currentKernelId) {
    try {
      await getKernel(currentKernelId, projectId);
      return currentKernelId;
    } catch (e: any) {
      if (e?.response?.status !== 404) {
        console.warn("[resolveJupyterKernelForExecution] getKernel failed:", e);
      }
    }
  }

  let sessions: Awaited<ReturnType<typeof listSessions>> = [];
  try {
    sessions = await listSessions(projectId);
  } catch (e) {
    console.warn("[resolveJupyterKernelForExecution] listSessions failed:", e);
  }

  const nbNorm = normalizeJupyterContentPath(notebookPath);
  const nbBase = nbNorm.split("/").pop() || "";

  const match =
    sessions.find((s) => normalizeJupyterContentPath(s.path) === nbNorm) ||
    sessions.find((s) => {
      const sp = normalizeJupyterContentPath(s.path);
      return sp === nbBase || sp.endsWith(`/${nbBase}`);
    }) ||
    sessions.find((s) => s.path?.includes(projectId)) ||
    (sessions.length === 1 ? sessions[0] : undefined);

  if (match?.kernel?.id) {
    return match.kernel.id;
  }

  try {
    const created = await createSession(nbNorm, "python3", projectId);
    if (created?.kernel?.id) {
      return created.kernel.id;
    }
  } catch (e) {
    console.error("[resolveJupyterKernelForExecution] createSession failed:", e);
  }

  return null;
}

// --- CHILD COMPONENTS ---

const ProjectExplorer: React.FC<any> = ({
  files,
  isLoadingFiles,
  onFileSelect,
  onFileCreate,
  onFileDelete,
  selectedFilePath,
  isDeleting,
  initialFile,
  isStudent,
  recentlyDeletedPath,
  onCollapseAll,
  isDarkMode,
  onRefresh,
  refreshTrigger,
  expandedDirectories,
  setExpandedDirectories,
}) => {
  const navigate = useNavigate();
  const { workspace, isWorkspaceReady, updateActivity } = useJupyterWorkspace();
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isCreatingFile, setIsCreatingFile] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    try {
      const refetchResult = await onRefresh();

      // Build the set of paths to refresh: all currently expanded dirs PLUS
      // any auto-expanded standard subdirs (datasets, notebooks) that may not
      // have finished loading yet when this runs.
      const rootFiles: FileItem[] = (refetchResult as any)?.data ?? [];
      const autoExpandPaths = rootFiles
        .filter((f: FileItem) => {
          const n = f.name?.toLowerCase();
          return f.type === "directory" && (n === "datasets" || n === "notebooks");
        })
        .map((f: FileItem) => f.path);

      const pathsToRefresh = Array.from(
        new Set([...Object.keys(expandedDirectories), ...autoExpandPaths])
      );

      if (pathsToRefresh.length > 0) {
        const refreshedDirs: Record<string, FileItem[]> = { ...expandedDirectories };
        await Promise.all(
          pathsToRefresh.map(async (path) => {
            try {
              const res = await getContents(path, workspace.workspaceType === 'local');
              refreshedDirs[path] = (res.content || []).sort(sortFiles);
            } catch (err) {
              console.error(`Failed to refresh directory ${path}`, err);
            }
          })
        );
        setExpandedDirectories(refreshedDirs);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-refresh when triggered externally (after upload or delete)
  useEffect(() => {
    if (refreshTrigger) handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  // Track which directories are expanded/collapsed for nested folder collapse
  const [expandedFolderPaths, setExpandedFolderPaths] = useState<
    Record<string, boolean>
  >({});

  // Auto-refresh periodically if any visible file is still syncing
  useEffect(() => {
    // Check root files and all expanded directories for syncing status
    const hasSyncingFiles = () => {
      // Check root files
      if (files.some(f => isFileStillDownloading(f))) return true;
      
      // Check all expanded directories
      return Object.values(expandedDirectories).some((dirFiles: FileItem[]) => 
        dirFiles.some(f => isFileStillDownloading(f))
      );
    };

    if (hasSyncingFiles()) {
      const interval = setInterval(() => {
        handleRefresh();
      }, 15000); // 15 seconds
      return () => clearInterval(interval);
    }
    return undefined;
  }, [files, expandedDirectories, workspace.workspaceType]);

  // Collapse all sections when triggered
  useEffect(() => {
    if (onCollapseAll) {
      setExpandedSections({});
      setExpandedDirectories({});
      setExpandedFolderPaths({});
    }
  }, [onCollapseAll]);

  // Initialize expanded sections based on available file types
  useEffect(() => {
    let hasChanges = false;
    const sections: Record<string, boolean> = { ...expandedSections };

    files.forEach((file: FileItem) => {
      const lowerName = file.name?.toLowerCase();
      if (file.type === "notebook") {
        if (sections["notebooks"] === undefined) {
          sections["notebooks"] = true;
          hasChanges = true;
        }
      } else if (file.type === "directory") {
        if (lowerName === "notebooks") {
          if (sections["notebooks"] === undefined) {
            sections["notebooks"] = true;
            hasChanges = true;
          }
        } else if (lowerName === "datasets") {
          if (sections["datasets"] === undefined) {
            sections["datasets"] = true;
            hasChanges = true;
          }
        } else {
          if (sections[file.name] === undefined) {
            sections[file.name] = false;
            hasChanges = true;
          }
        }
      } else if (file.type === "file") {
        if (file.name.endsWith(".py")) {
          if (sections["pythonScripts"] === undefined) {
            sections["pythonScripts"] = false;
            hasChanges = true;
          }
        } else {
          if (sections["otherFiles"] === undefined) {
            sections["otherFiles"] = false;
            hasChanges = true;
          }
        }
      }
    });

    if (hasChanges) {
      setExpandedSections(sections);
    }
  }, [files]);

  const handleDirectoryClick = async (directory: FileItem) => {
    const path = directory.path;

    // If it's already expanded, collapse it by removing from state
    if (expandedDirectories[path]) {
      // Just collapse - remove from expanded directories
      setExpandedDirectories((prev) => {
        const newState = { ...prev };
        delete newState[path];
        return newState;
      });
      return;
    }

    // Otherwise, load the contents and expand
    const parts = path.split("/");

    try {
      const res = await getContents(path, workspace.workspaceType === 'local');
      let contents = res.content || [];

      // Linked datasets are synced to the Jupyter root (e.g. `datasets/Title/`)
      // but the workspace-files sync may have created an empty shadow directory at
      // `projectId/datasets/Title/` (files skipped due to s3fs symlink resolution).
      // Fall back to the root-relative path so the explorer shows the real files.
      if (contents.length === 0 && parts.length > 2) {
        try {
          const rootPath = parts.slice(1).join("/");
          const rootRes = await getContents(rootPath);
          if ((rootRes.content || []).length > 0) {
            contents = rootRes.content;
          }
        } catch (_) { /* ignore — keep contents as [] */ }
      }

      setExpandedDirectories((prev) => ({
        ...prev,
        [path]: contents.sort(sortFiles),
      }));
    } catch (err) {
      toast.error("Failed to load directory contents");
    }
  };

  const toggleSection = async (section: string, directory?: FileItem) => {
    const isExpanding = !expandedSections[section];
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

    // If expanding and it's a directory, fetch its contents
    if (isExpanding && directory && directory.type === "directory") {
      await handleDirectoryClick(directory);
    }
  };

  // Auto-expand notebooks and datasets directories on load
  useEffect(() => {
    if (files.length > 0) {
      files.forEach((file: FileItem) => {
        const lowerName = file.name?.toLowerCase();
        if (
          file.type === "directory" &&
          (lowerName === "notebooks" || lowerName === "datasets")
        ) {
          if (!expandedDirectories[file.path]) {
            handleDirectoryClick(file);
          }
        }
      });
    }
  }, [files]);

  const handleCreateNewFile = async (
    fileType: "notebook" | "markdown" | "text" | "python",
  ) => {
    if (isCreatingFile) return;

    setIsCreatingFile(true);
    setError(null);
    try {
      let ext = ".ipynb";
      if (fileType === "python") ext = ".py";
      if (fileType === "markdown") ext = ".md";
      if (fileType === "text") ext = ".txt";

      const existingNames = files.map((f: FileItem) => f.name);
      const newName = getUniqueFilename(existingNames, "Untitled", ext);

      await onFileCreate(fileType, newName);
    } catch (err) {
      setError(`Failed to create ${fileType} file.`);
    } finally {
      setIsCreatingFile(false);
    }
  };

  const handleCopyPath = (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    const relativePath = pathRelativeToNotebookRoot(
      path,
      workspace.folderPath,
      workspace.workspaceType,
      workspace.project?.id,
    );

    navigator.clipboard
      .writeText(relativePath)
      .then(() => toast.success(`Copied: ${relativePath}`))
      .catch((err) => {
        console.error("Failed to copy path:", err);
        toast.error("Failed to copy.");
      });
  };

  const samePath = (a?: string, b?: string) => !!a && !!b && a === b;

  const filteredFiles = files
    .filter((f: FileItem) =>
      f.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((f: FileItem) => f.path !== recentlyDeletedPath);

  // Categorize files dynamically based on API response
  const categorizeFiles = () => {
    const categories: Record<string, FileItem[]> = {};

    filteredFiles.forEach((file: FileItem) => {
      if (file.type === "notebook") {
        if (!categories["notebooks"]) categories["notebooks"] = [];
        categories["notebooks"].push(file);
      } else if (file.type === "directory") {
        // Special handling for datasets and notebooks directories
        if (file.name.toLowerCase() === "datasets") {
          if (!categories["datasets"]) categories["datasets"] = [];
          categories["datasets"].push(file);
        } else if (file.name.toLowerCase() === "notebooks") {
          if (!categories["notebooks"]) categories["notebooks"] = [];
          categories["notebooks"].push(file);
        } else {
          // Other directories become their own section
          if (!categories[file.name]) categories[file.name] = [];
          categories[file.name].push(file);
        }
      } else if (file.type === "file") {
        if (file.name.endsWith(".py")) {
          if (!categories["pythonScripts"]) categories["pythonScripts"] = [];
          categories["pythonScripts"].push(file);
        } else {
          if (!categories["otherFiles"]) categories["otherFiles"] = [];
          categories["otherFiles"].push(file);
        }
      }
    });

    // Sort each category using sortFiles (directories first, then by recent)
    Object.keys(categories).forEach((key) => {
      categories[key] = categories[key].sort(sortFiles);
      // Limit to 5 items for students
      if (isStudent) {
        categories[key] = categories[key].slice(0, 5);
      }
    });

    return categories;
  };

  const fileCategories = categorizeFiles();

  // Define the order of sections
  const sectionOrder = ["notebooks", "datasets", "pythonScripts"];

  // Get ordered sections that exist in fileCategories
  const orderedSections = sectionOrder.filter((key) => fileCategories[key]);

  // Add any other sections not in the predefined order
  const otherSections = Object.keys(fileCategories).filter(
    (key) => !sectionOrder.includes(key),
  );

  const allSections = [...orderedSections, ...otherSections];

  // Add initialFile to appropriate category if not already present
  if (initialFile && initialFile.path) {
    const addToCategory = (categoryKey: string) => {
      if (!fileCategories[categoryKey]) fileCategories[categoryKey] = [];
      if (
        !fileCategories[categoryKey].some((f: FileItem) =>
          samePath(f.path, initialFile.path),
        )
      ) {
        fileCategories[categoryKey] = [
          ...fileCategories[categoryKey],
          initialFile,
        ];
      }
    };

    if (initialFile.type === "notebook") {
      addToCategory("notebooks");
    } else if (initialFile.type === "directory") {
      addToCategory(initialFile.name);
    } else if (initialFile.type === "file") {
      if (initialFile.name.endsWith(".py")) {
        addToCategory("pythonScripts");
      } else {
        addToCategory("otherFiles");
      }
    }
  }

  // Helper: a file in a datasets/ path that has a 1970 
  // timestamp is a placeholder or pointer currently being synced.
  const isFileStillDownloading = (file: FileItem): boolean => {
    if (file.type === "directory") return false;
    
    // Check for 1970 timestamp (Magic Date set by init.sh for placeholders)
    // This is the only 100% reliable signal for the tree badge.
    const isMagicDate = file.last_modified?.startsWith("1970-01-01");
    return !!isMagicDate;
  };

  const renderFileItem = (file: FileItem) => {
    const isExpanded = !!expandedDirectories[file.path];
    const stillDownloading = isFileStillDownloading(file);

    return (
      <div key={file.path}>
        <div
          className={`group flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-[#21262d] rounded-md ${
            selectedFilePath === file.path ? "bg-blue-100 dark:bg-blue-900/30" : ""
          }`}
          onClick={
            file.type === "directory"
              ? () => handleDirectoryClick(file)
              : () => onFileSelect({ path: file.path, type: file.type })
          }
        >
          {/* Chevron icon for directories */}
          {file.type === "directory" ? (
            <div className="flex-shrink-0">
              {isExpanded ? (
                <ChevronDown size={14} className="text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronRight size={14} className="text-gray-500 dark:text-gray-400" />
              )}
            </div>
          ) : (
            <div className="w-[14px] flex-shrink-0" />
          )}
          {getFileIcon(file)}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 dark:text-[#f0f6fc] truncate">{file.name}</p>
            {stillDownloading ? (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <Loader2 size={9} className="animate-spin" />
                Syncing…
              </span>
            ) : (
              <p className="text-xs text-gray-500 dark:text-[#8b949e]">
                {formatFileSize(file.size)} • {formatTimeAgo(file.last_modified)}
              </p>
            )}
          </div>
          {file.type !== "directory" && (
            <>
              <button
                onClick={(e) => handleCopyPath(e, file.path)}
                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-300 dark:hover:bg-[#30363d] transition-opacity"
                title="Copy path"
              >
                <Copy size={14} className="text-gray-600 dark:text-gray-400" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFileDelete(file.path);
                }}
                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-300 dark:hover:bg-red-900/30 transition-opacity"
                title="Delete file"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-red-600"></div>
                ) : (
                  <Trash2 size={14} className="text-red-600 dark:text-red-500" />
                )}
              </button>
            </>
          )}
        </div>
        {file.type === "directory" && isExpanded && (
          <div className="ml-6 mt-1 border-l border-gray-100 dark:border-[#30363d] pl-2">
            {expandedDirectories[file.path] ? (
              expandedDirectories[file.path].map((subFile) =>
                renderFileItem(subFile),
              )
            ) : (
              <p className="text-xs text-gray-400 pl-4 py-1">Loading...</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (
    title: string,
    sectionKey: keyof typeof expandedSections,
    fileList: FileItem[],
  ) => {
    // Check if this section represents a single directory
    const isDirectorySection =
      fileList.length === 1 && fileList[0].type === "directory";
    const directory = isDirectorySection ? fileList[0] : null;

    const getSectionDisplayName = (key: string) => {
      switch (key) {
        case "notebooks":
          return "Course Notebooks";
        case "datasets":
          return "Course Datasets";
        case "pythonScripts":
          return "Python Scripts";
        case "otherFiles":
          return "Other Files";
        default:
          // For directory names, capitalize first letter
          return key.charAt(0).toUpperCase() + key.slice(1);
      }
    };

    const getSectionIcon = (key: string) => {
      switch (key) {
        case "notebooks":
          return (
            <FileText size={16} className="text-orange-500 flex-shrink-0" />
          );
        case "datasets":
          return <Database size={16} className="text-blue-500 flex-shrink-0" />;
        case "pythonScripts":
          return (
            <FileCode size={16} className="text-green-500 flex-shrink-0" />
          );
        case "otherFiles":
          return <File size={16} className="text-gray-500 flex-shrink-0" />;
        default:
          // For other directories
          return <Folder size={16} className="text-yellow-500 flex-shrink-0" />;
      }
    };

    const handleNavigate = () => {
      const projectId = workspace?.project?.id;
      if (sectionKey === "notebooks") {
        navigate(paths.notebooks.replace(":projectId", projectId!));
      } else if (sectionKey === "pythonScripts") {
        navigate(paths.pythonScripts.replace(":projectId", projectId!));
      }
      // Add other navigation paths as needed
    };

    const displayName = getSectionDisplayName(sectionKey);

    return (
      <div key={sectionKey}>
        <button
          onClick={() => toggleSection(sectionKey, directory || undefined)}
          className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#21262d] p-2 rounded-md transition-colors group"
        >
          <div className="flex items-center gap-2">
            {getSectionIcon(sectionKey)}
            <span className="font-medium">{displayName}</span>
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 text-xs bg-gray-200 dark:bg-[#30363d] text-gray-600 dark:text-gray-400 rounded-full px-2">
              {isDirectorySection && expandedDirectories[directory!.path]
                ? expandedDirectories[directory!.path].length
                : fileList.length}
            </span>
          </div>
          <div className="flex items-center">
            {expandedSections[sectionKey] ? (
              <ChevronDown size={16} className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
            ) : (
              <ChevronRight size={16} className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
            )}
          </div>
        </button>
        {expandedSections[sectionKey] && (
          <div className="pl-6 mt-1 space-y-1">
            {isDirectorySection && directory ? (
              // For directory sections, show the contents directly
              expandedDirectories[directory.path] &&
              expandedDirectories[directory.path].length > 0 ? (
                expandedDirectories[directory.path].map(renderFileItem)
              ) : (
                <p className="text-xs text-gray-400 pl-2">Loading...</p>
              )
            ) : // For non-directory sections, show files normally
            fileList.length > 0 ? (
              fileList.map(renderFileItem)
            ) : (
              <p className="text-xs text-gray-400 pl-2">No files found.</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="h-full flex flex-col w-full space-y-4">
      {/* Project Explorer Section */}
      <div className="p-4 pb-3">
        <div className="w-full text-lg font-semibold text-gray-900 dark:text-[#f0f6fc] p-2 flex justify-between items-center">
          <span>Project Explorer</span>
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-[#30363d] transition-colors ${
                isRefreshing
                  ? "text-blue-500"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
              title="Refresh files"
            >
              <RotateCcw size={16} className={isRefreshing ? "animate-spin" : ""} />
            </button>
          )}
        </div>

        <div className="space-y-4 mt-4">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search dataset, notebooks, etc..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-[#30363d] rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-[#f0883e] text-sm bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc]"
            />
          </div>
          {isStudent && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-center dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
                  disabled={isCreatingFile}
                >
                  {isCreatingFile ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={16} className="mr-2" />
                      New
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] dark:bg-[#161b22] dark:border-[#30363d]">
                <DropdownMenuItem
                  onClick={() => handleCreateNewFile("notebook")}
                  className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  <span>Notebook</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleCreateNewFile("python")}
                  className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]"
                >
                  <FileCode className="mr-2 h-4 w-4" />
                  <span>Python Script</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleCreateNewFile("markdown")}
                  className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  <span>Markdown File</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleCreateNewFile("text")}
                  className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]"
                >
                  <File className="mr-2 h-4 w-4" />
                  <span>Text File</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div className="space-y-2">
            {error ? (
              <p className="text-red-500">{error}</p>
            ) : (
              <>
                {allSections.map((categoryKey) =>
                  renderSection(
                    categoryKey,
                    categoryKey,
                    fileCategories[categoryKey],
                  ),
                )}
                {allSections.length === 0 && (
                  isLoadingFiles ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                      Loading files...
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No files found
                    </p>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

const EditableFileViewer: React.FC<any> = ({
  file,
  content,
  onContentChange,
  onRename,
  isRenaming: isRenamingProp,
  isStudent,
  isSidebarCollapsed,
  onToggleSidebar,
  isDarkMode,
}) => {
  const getNameAndExt = (fullName: string) => {
    const lastDot = fullName.lastIndexOf(".");
    if (lastDot === -1) return { name: fullName, ext: "" };
    return {
      name: fullName.substring(0, lastDot),
      ext: fullName.substring(lastDot),
    };
  };

  const [isRenaming, setIsRenaming] = useState(false);
  const [fileName, setFileName] = useState(file.name);
  const { name: originalName, ext: fileExt } = getNameAndExt(file.name);
  const [nameOnly, setNameOnly] = useState(originalName);

  // Update fileName and nameOnly when file changes
  useEffect(() => {
    setFileName(file.name);
    const { name } = getNameAndExt(file.name);
    setNameOnly(name);
  }, [file.name]);

  const handleRename = async () => {
    const trimmedName = nameOnly.trim();
    const newFullName = trimmedName + fileExt;
    if (!trimmedName || newFullName === fileName) {
      setIsRenaming(false);
      setNameOnly(getNameAndExt(fileName).name);
      return;
    }
    const temp = fileName;
    setFileName(newFullName);
    try {
      await onRename(newFullName);
      setNameOnly(getNameAndExt(newFullName).name);
    } catch (error) {
      setFileName(temp);
      setNameOnly(getNameAndExt(fileName).name);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-[#0d1117]">
      <div className="flex-shrink-0 flex items-center px-6 py-3 border-b bg-white dark:bg-[#161b22] dark:border-[#30363d]">
        {isRenaming ? (
          <div className="flex items-center">
            <AutoSizeInput
              type="text"
              value={nameOnly}
              onChange={(e) => setNameOnly(e.target.value)}
              onBlur={handleRename}
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
              className="font-medium text-gray-800 dark:text-[#f0f6fc] focus:outline-none bg-transparent"
              autoFocus
              disabled={isRenamingProp}
            />
            <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileExt}</span>
            {isRenamingProp && (
              <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
            )}
          </div>
        ) : (
          <>
            <h3 
              className="font-medium text-gray-800 dark:text-[#f0f6fc] cursor-pointer hover:text-blue-600 dark:hover:text-[#f0883e] transition-colors select-none"
              onDoubleClick={() => setIsRenaming(true)}
              title="Double click to rename"
            >
              {fileName}
            </h3>
            {isStudent && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRenaming(true)}
                className="ml-2 dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
              >
                {isRenamingProp ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                ) : (
                  <Edit className="text-gray-800 dark:text-[#c9d1d9]" size={14} />
                )}
              </Button>
            )}
          </>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {file?.name?.endsWith(".py") ? (
          // Python file with syntax highlighting
          <CodeMirror
            value={content}
            height="100%"
            extensions={[python(), EditorView.lineWrapping, selectionHighlight]}
            theme={isDarkMode ? oneDark : githubLight}
            onChange={(value) => onContentChange(value)}
            readOnly={!isStudent}
            basicSetup={{
              lineNumbers: true,
              highlightActiveLineGutter: true,
              highlightActiveLine: true,
              foldGutter: true,
              dropCursor: true,
              indentOnInput: true,
              syntaxHighlighting: true,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: true,
              rectangularSelection: true,
              highlightSelectionMatches: true,
            }}
            style={{
              fontSize: "14px",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          />
        ) : (
          // Plain text editor for other file types
          <textarea
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            className="w-full h-full p-6 font-mono text-sm border-none focus:outline-none resize-none bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc]"
            autoFocus
            readOnly={!isStudent}
          />
        )}
      </div>
    </div>
  );
};

const ViewOnlyViewer: React.FC<any> = ({
  file,
  onRename,
  isRenaming: isRenamingProp,
  isStudent,
  isSidebarCollapsed,
  onToggleSidebar,
  isDarkMode,
}) => {
  const getNameAndExt = (fullName: string) => {
    const lastDot = fullName.lastIndexOf(".");
    if (lastDot === -1) return { name: fullName, ext: "" };
    return {
      name: fullName.substring(0, lastDot),
      ext: fullName.substring(lastDot),
    };
  };

  const [isRenaming, setIsRenaming] = useState(false);
  const [fileName, setFileName] = useState(file.name);
  const { name: originalName, ext: fileExt } = getNameAndExt(file.name);
  const [nameOnly, setNameOnly] = useState(originalName);

  // Update fileName and nameOnly when file changes
  useEffect(() => {
    setFileName(file.name);
    const { name } = getNameAndExt(file.name);
    setNameOnly(name);
  }, [file.name]);

  const handleRename = async () => {
    const trimmedName = nameOnly.trim();
    const newFullName = trimmedName + fileExt;
    if (!trimmedName || newFullName === fileName) {
      setIsRenaming(false);
      setNameOnly(getNameAndExt(fileName).name);
      return;
    }
    const temp = fileName;
    setFileName(newFullName);
    try {
      await onRename(newFullName);
      setNameOnly(getNameAndExt(newFullName).name);
    } catch (error) {
      setFileName(temp);
      setNameOnly(getNameAndExt(fileName).name);
    } finally {
      setIsRenaming(false);
    }
  };

  const ImageViewer = ({ file }: { file: FileItem }) => {
    const imageUrl = `data:${file.mimetype};base64,${file.content}`;
    return (
      <div className="p-4 flex items-center justify-center bg-white dark:bg-[#0d1117] h-full">
        <img
          src={imageUrl}
          alt={file.name}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );
  };

  const fileHeader = (
    <div className="flex-shrink-0 flex items-center px-6 py-3 border-b bg-white dark:bg-[#161b22] dark:border-[#30363d]">
      {isRenaming ? (
        <div className="flex items-center">
          <AutoSizeInput
            type="text"
            value={nameOnly}
            onChange={(e) => setNameOnly(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
            className="font-medium text-gray-800 dark:text-[#f0f6fc] focus:outline-none bg-transparent"
            autoFocus
            disabled={isRenamingProp}
          />
          <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileExt}</span>
          {isRenamingProp && (
            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
          )}
        </div>
      ) : (
        <>
            <h3 
              className="font-medium text-gray-800 dark:text-[#f0f6fc] cursor-pointer hover:text-blue-600 dark:hover:text-[#f0883e] transition-colors select-none"
              onDoubleClick={() => setIsRenaming(true)}
              title="Double click to rename"
            >
              {fileName}
            </h3>
          {isStudent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsRenaming(true)}
              className="ml-2 dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
            >
              {isRenamingProp ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              ) : (
                <Edit className="text-gray-800 dark:text-[#c9d1d9]" size={14} />
              )}
            </Button>
          )}
        </>
      )}
    </div>
  );

  const extension = file?.name?.split(".").pop()?.toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "svg"].includes(extension || "")) {
    if (typeof file.content === "string") {
      return (
        <div className="flex flex-col h-full bg-white dark:bg-[#0d1117]">
          {fileHeader}
          <ImageViewer file={file} />
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-[#0d1117]">
      {fileHeader}
      <div className="flex-1 flex items-center justify-center text-center text-gray-600 dark:text-[#8b949e]">
        <div>
          <h3 className="text-lg font-semibold dark:text-[#f0f6fc]">Preview Not Available</h3>
          <p className="mt-1">Viewing for '{file.name}' is not supported.</p>
        </div>
      </div>
    </div>
  );
};

const NotebookContent: React.FC<{
  notebook: Notebook;
  initialNotebook?: Notebook;
  onRename: (newName: string) => Promise<void>;
  cells: Cell[];
  onCellsChange: React.Dispatch<SetStateAction<Cell[]>>;
  workspace: any;
  isWorkspaceReady: boolean;
  updateActivity: () => void;
  executeCellMutation: any;
  executeNotebookMutation: any;
  isRenaming?: boolean;
  isStudent: boolean;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onShare?: () => void;
  onSave?: () => void;
  onUpload?: () => void;
  onSubmit?: () => void;
  isDirty?: boolean;
  isSaving?: boolean;
  onKernelStatusChange?: (status: "idle" | "busy" | "starting") => void;
  isDarkMode: boolean;
  /** When kernelId is missing or stale, recover before execute (reload / session drift). */
  executionRecovery?: {
    projectId: string | undefined;
    updateWorkspace: (u: { kernelId?: string | null }) => void;
    validateSession: () => Promise<boolean>;
  };
}> = ({
  notebook: initialNotebook,
  onRename,
  cells,
  onCellsChange,
  workspace,
  isWorkspaceReady,
  updateActivity,
  executeCellMutation,
  executeNotebookMutation,
  executionRecovery,
  isRenaming: isRenamingProp,
  isStudent,
  isSidebarCollapsed,
  onToggleSidebar,
  onShare,
  onSave,
  onUpload,
  onSubmit,
  isDirty,
  isSaving,
  onKernelStatusChange,
  isDarkMode,
}) => {
  const [kernelStatus, setKernelStatus] = useState<
    "idle" | "busy" | "starting"
  >("idle");

  useEffect(() => {
    onKernelStatusChange?.(kernelStatus);
  }, [kernelStatus, onKernelStatusChange]);

  const [error, setError] = useState<string | null>(null);
  const [activeCell, setActiveCell] = useState<string | null>(null);
  const notebookShellRef = useRef<HTMLDivElement | null>(null);
  const [editingMarkdownCell, setEditingMarkdownCell] = useState<string | null>(
    null,
  );
  const [isRenaming, setIsRenaming] = useState(false);
  const [executionStatuses, setExecutionStatuses] = useState<
    Record<string, "Executing" | "Executed">
  >({});
  const interruptKernelMutation = useInterruptKernelMutation();
  const restartKernelMutation = useRestartKernelMutation();
  const getNameAndExt = (fullName: string) => {
    const lastDot = fullName.lastIndexOf(".");
    if (lastDot === -1) return { name: fullName, ext: "" };
    return {
      name: fullName.substring(0, lastDot),
      ext: fullName.substring(lastDot),
    };
  };
  const [fileName, setFileName] = useState(initialNotebook.name);
  const { name: originalName, ext: fileExt } = getNameAndExt(
    initialNotebook.name,
  );
  const [nameOnly, setNameOnly] = useState(originalName);

  // Update fileName and nameOnly when notebook changes
  useEffect(() => {
    setFileName(initialNotebook.name);
    const { name } = getNameAndExt(initialNotebook.name);
    setNameOnly(name);
  }, [initialNotebook.name]);

  // map of cellId -> textarea ref
  const cellRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  // choose first cell as active on mount
  useEffect(() => {
    if (cells.length > 0 && !cells.find((c) => c.id === activeCell)) {
      setActiveCell(cells[0]?.id ?? null);
    }
  }, [cells, activeCell]);

  // ensure ui.showOutput exists
  useEffect(() => {
    const needsPatch = cells.some(
      (c) => c.type === "code" && c.ui?.showOutput === undefined,
    );
    if (needsPatch) {
      onCellsChange((prev) =>
        prev.map((c) =>
          c.type === "code" && c.ui?.showOutput === undefined
            ? { ...c, ui: { ...(c.ui || {}), showOutput: true } }
            : c,
        ),
      );
    }
  }, [cells, onCellsChange]);

  const handleRename = async () => {
    const trimmedName = nameOnly.trim();
    const newFullName = trimmedName + fileExt;
    if (!trimmedName || newFullName === fileName) {
      setIsRenaming(false);
      setNameOnly(getNameAndExt(fileName).name);
      return;
    }
    const temp = fileName;
    setFileName(newFullName);
    try {
      await onRename(newFullName);
      setNameOnly(getNameAndExt(newFullName).name);
    } catch (error) {
      setFileName(temp);
      setNameOnly(getNameAndExt(fileName).name);
    } finally {
      setIsRenaming(false);
    }
  };

  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const editorRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const focusCell = (cellId: string) => {
    const wrapper = containerRefs.current[cellId];
    const editor = editorRefs.current[cellId];
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (editor) {
      editor.focus();
      const len = editor.value?.length ?? 0;
      requestAnimationFrame(() => {
        try {
          editor.setSelectionRange(len, len);
        } catch {}
      });
    } else if (wrapper) {
      const cm = wrapper.querySelector(".cm-content") as HTMLElement | null;
      if (cm && typeof cm.focus === "function") {
        cm.focus();
      } else {
        wrapper.focus();
      }
    }
  };

  const goToAdjacentCell = (delta: -1 | 1) => {
    if (!cells.length) return;
    const baseIdx = activeCell
      ? cells.findIndex((c) => c.id === activeCell)
      : 0;
    const idx = baseIdx < 0 ? 0 : baseIdx;
    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= cells.length) return;
    const nextId = cells[nextIdx].id;
    setEditingMarkdownCell(null);
    setActiveCell(nextId);
    setTimeout(() => focusCell(nextId), 0);
  };

  const addCell = (
    type: "code" | "markdown",
    afterId: string | null,
    insertIndex?: number,
  ) => {
    const newCell: Cell = {
      id: `cell_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type,
      content: "",
      output: [],
      execution_count: null,
      metadata: {},
      ui: { showOutput: true },
    };

    let newCells: Cell[] = [];

    if (typeof insertIndex === "number") {
      // explicit index insert (handles top-of-list case)
      newCells = [...cells];
      newCells.splice(insertIndex, 0, newCell);
    } else if (afterId) {
      const index = cells.findIndex((cell) => cell.id === afterId);
      newCells =
        index === -1
          ? [...cells, newCell]
          : [...cells.slice(0, index + 1), newCell, ...cells.slice(index + 1)];
    } else {
      newCells = [...cells, newCell];
    }

    onCellsChange(newCells);
    setActiveCell(newCell.id);
    requestAnimationFrame(() => focusCell(newCell.id));
  };

  const deleteCell = (cellId: string) => {
    onCellsChange(cells.filter((cell) => cell.id !== cellId));
  };

  const updateCell = (cellId: string, content: string) => {
    onCellsChange(
      cells.map((cell) => (cell.id === cellId ? { ...cell, content } : cell)),
    );
  };

  const moveCellUp = (cellId: string) => {
    onCellsChange((prev) => {
      const idx = prev.findIndex((c) => c.id === cellId);
      if (idx <= 0) return prev;
      const copy = [...prev];
      const m = copy.splice(idx, 1)[0];
      copy.splice(idx - 1, 0, m);
      return copy;
    });
    setActiveCell(cellId);
    requestAnimationFrame(() => focusCell(cellId));
  };

  const moveCellDown = (cellId: string) => {
    onCellsChange((prev) => {
      const idx = prev.findIndex((c) => c.id === cellId);
      if (idx === -1 || idx === prev.length - 1) return prev;
      const copy = [...prev];
      const m = copy.splice(idx, 1)[0];
      copy.splice(idx + 1, 0, m);
      return copy;
    });
    setActiveCell(cellId);
    requestAnimationFrame(() => focusCell(cellId));
  };

  const toggleOutput = (cellId: string) => {
    onCellsChange((prev) =>
      prev.map((c) =>
        c.id === cellId
          ? {
              ...c,
              ui: { ...(c.ui || {}), showOutput: !(c.ui?.showOutput ?? true) },
            }
          : c,
      ),
    );
  };

  const clearOutput = (cellId: string) => {
    onCellsChange((prev) =>
      prev.map((c) =>
        c.id === cellId ? { ...c, output: [], execution_count: null } : c,
      ),
    );
  };

  const executeCell = async (cellId: string) => {
    if (isSaving) {
      toast.error("Please wait until the file is saved.");
      return;
    }
    const cell = cells.find((c) => c.id === cellId);
    if (!cell || cell.type !== "code") {
      setError("Cannot execute: Cell not found or not a code cell.");
      return;
    }

    setExecutionStatuses((prev) => ({ ...prev, [cellId]: "Executing" }));

    let kernelId: string | null | undefined = workspace.kernelId;

    if ((!kernelId || !isWorkspaceReady) && executionRecovery?.projectId) {
      toast.loading("Connecting to kernel…", { id: "kernel-recover-exec" });
      try {
        try {
          await executionRecovery.validateSession();
        } catch {
          /* session probe is best-effort */
        }
        const resolved = await resolveJupyterKernelForExecution({
          projectId: executionRecovery.projectId,
          notebookPath: initialNotebook.path,
          currentKernelId: kernelId,
        });
        if (resolved) {
          executionRecovery.updateWorkspace({ kernelId: resolved });
          kernelId = resolved;
        }
      } finally {
        toast.dismiss("kernel-recover-exec");
      }
    }

    if (!kernelId) {
      setExecutionStatuses((prev) => {
        const next = { ...prev };
        delete next[cellId];
        return next;
      });
      setError("Not connected to JupyterLab. Please check the connection.");
      return;
    }

    setError(null);
    updateActivity();

    // optimistic execution count increment
    onCellsChange((currentCells) =>
      currentCells.map((c) =>
        c.id === cellId
          ? {
              ...c,
              output: [],
              executioncount:
                typeof c.execution_count === "number"
                  ? c.execution_count + 1
                  : 1,
            }
          : c,
      ),
    );

    try {
      const kernalInfo = await getKernel(
        kernelId,
        executionRecovery?.projectId,
      );
      if (kernalInfo.execution_state === "busy") {
        await interruptKernel(kernelId);
      }
      setKernelStatus("busy");

      const response = await executeCellMutation.mutateAsync({
        kernelId,
        request: { code: cell.content, timeout: 30000 },
      });

      onCellsChange((currentCells) =>
        currentCells.map((c) =>
          c.id === cellId
            ? {
                ...c,
                output: response?.data?.outputs || [],
                executioncount: response?.data?.execution_count ?? null,
              }
            : c,
        ),
      );
      setKernelStatus("idle");
    } catch (error: any) {
      setError(`Failed to execute cell: ${error?.message ?? "Unknown error"}`);
      setKernelStatus("idle");
      onCellsChange((currentCells) =>
        currentCells.map((c) =>
          c.id === cellId
            ? {
                ...c,
                output: [
                  {
                    output_type: "error",
                    ename: "ExecutionError",
                    evalue: error?.message ?? "Unknown error",
                    traceback: [],
                  },
                ],
              }
            : c,
        ),
      );
    } finally {
      setExecutionStatuses((prev) => ({ ...prev, [cellId]: "Executed" }));
    }
  };

  const executeAllCells = async () => {
    if (isSaving) {
      toast.error("Please wait until the file is saved.");
      return;
    }

    let kernelId: string | null | undefined = workspace.kernelId;

    if ((!kernelId || !isWorkspaceReady) && executionRecovery?.projectId) {
      toast.loading("Connecting to kernel…", { id: "kernel-recover-exec-all" });
      try {
        try {
          await executionRecovery.validateSession();
        } catch {
          /* ignore */
        }
        const resolved = await resolveJupyterKernelForExecution({
          projectId: executionRecovery.projectId,
          notebookPath: initialNotebook.path,
          currentKernelId: kernelId,
        });
        if (resolved) {
          executionRecovery.updateWorkspace({ kernelId: resolved });
          kernelId = resolved;
        }
      } finally {
        toast.dismiss("kernel-recover-exec-all");
      }
    }

    if (!kernelId) {
      setError("Not connected to JupyterLab. Please check the connection.");
      return;
    }

    const codeCells = cells.filter((cell) => cell.type === "code");
    setExecutionStatuses((prev) => {
      const newStatuses = { ...prev };
      codeCells.forEach((cell) => (newStatuses[cell.id] = "Executing"));
      return newStatuses;
    });

    try {
      const kernalInfo = await getKernel(
        kernelId,
        executionRecovery?.projectId,
      );
      if (kernalInfo.execution_state === "busy") {
        await interruptKernel(kernelId);
      }
      setKernelStatus("busy");
      setError(null);
      updateActivity();

      const response = await executeNotebookMutation.mutateAsync({
        kernelId,
        request: {
          notebook: {
            cells: codeCells.map((cell) => ({
              cell_type: cell.type,
              source: Array.isArray(cell.content) ? cell.content : cell.content,
            })),
          },
          timeout: 60000,
          stopOnError: true,
        },
      });

      if (response.isSuccess && response.data) {
        onCellsChange((currentCells) =>
          currentCells.map((cell) => {
            const idx = codeCells.findIndex((c) => c.id === cell.id);
            const result = response.data.results.find(
              (r: any) => r.cell_index === idx,
            );
            if (result && cell.type === "code") {
              return {
                ...cell,
                output: result.outputs,
                executioncount: result.execution_count,
              };
            }
            return cell;
          }),
        );
      }

      setKernelStatus("idle");
    } catch (error: any) {
      setError(
        `Failed to execute notebook: ${error?.message ?? "Unknown error"}`,
      );
      setKernelStatus("idle");
    } finally {
      setExecutionStatuses((prev) => {
        const newStatuses = { ...prev };
        codeCells.forEach((cell) => (newStatuses[cell.id] = "Executed"));
        return newStatuses;
      });
    }
  };

  const interruptKernel = async (overrideKernelId?: string | null) => {
    const kid = overrideKernelId ?? workspace.kernelId;
    if (!kid) return;
    try {
      await interruptKernelMutation.mutateAsync(kid);
      setKernelStatus("idle");
    } catch (err) {
      console.error("Error interrupting kernel:", err);
    }
  };

  const restartKernel = async () => {
    const kernelId = workspace.kernelId;
    if (!kernelId) return;
    try {
      setKernelStatus("starting");
      await restartKernelMutation.mutateAsync(kernelId);
      setKernelStatus("idle");
      onCellsChange((cells) =>
        cells.map((cell) => ({ ...cell, executioncount: null, output: [] })),
      );
    } catch (err) {
      console.error("Error restarting kernel:", err);
      setKernelStatus("idle");
    }
  };

  // Unified output renderer: single bordered box per cell
  const renderCellOutputItemsUnified = (output: any[] = []) => {
    if (!Array.isArray(output) || output.length === 0) return null;
    return (
      <div>
        {output.map((out: any, index: number) => {
          if (
            out.output_type === "execute_result" ||
            out.output_type === "display_data"
          ) {
            if (out.data?.["image/png"]) {
              return (
                <div key={index} className="mb-3 last:mb-0">
                  <img
                    src={`data:image/png;base64,${out.data["image/png"]}`}
                    alt="cell output"
                    className="max-w-full h-auto"
                  />
                </div>
              );
            }
            if (out.data?.["text/plain"]) {
              return (
                <pre key={index} className="whitespace-pre-wrap mb-3 last:mb-0">
                  {Array.isArray(out.data["text/plain"])
                    ? out.data["text/plain"].join("")
                    : out.data["text/plain"]}
                </pre>
              );
            }
            return null;
          } else if (out.output_type === "stream") {
            return (
              <pre key={index} className="whitespace-pre-wrap mb-3 last:mb-0">
                {Array.isArray(out.text) ? out.text.join("") : out.text}
              </pre>
            );
          } else if (out.output_type === "error") {
            return (
              <div key={index} className="mb-3 last:mb-0">
                <div className="text-red-600 font-semibold">{out.ename}</div>
                <div className="text-red-700 whitespace-pre-wrap">
                  {out.evalue}
                </div>
                {out.traceback && (
                  <pre className="text-xs mt-2">{out.traceback.join("\n")}</pre>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const executeCellRef = useRef(executeCell);
  executeCellRef.current = executeCell;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const shell = notebookShellRef.current;
      const target = e.target as Node | null;
      const focusInsideNotebook = !!(
        shell &&
        target &&
        shell.contains(target)
      );

      if (
        (e.key === "s" || e.key === "S") &&
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        onSaveRef.current?.();
        return;
      }

      if (
        focusInsideNotebook &&
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        (e.key === "ArrowDown" || e.key === "ArrowUp")
      ) {
        const curId = activeCell ?? cells[0]?.id;
        if (!curId || !cells.length) return;
        const idx = cells.findIndex((c) => c.id === curId);
        if (idx < 0) return;
        if (e.key === "ArrowDown" && idx < cells.length - 1) {
          e.preventDefault();
          e.stopPropagation();
          setEditingMarkdownCell(null);
          const nextId = cells[idx + 1].id;
          setActiveCell(nextId);
          setTimeout(() => focusCell(nextId), 0);
          return;
        }
        if (e.key === "ArrowUp" && idx > 0) {
          e.preventDefault();
          e.stopPropagation();
          setEditingMarkdownCell(null);
          const prevId = cells[idx - 1].id;
          setActiveCell(prevId);
          setTimeout(() => focusCell(prevId), 0);
          return;
        }
      }

      if (!activeCell) return;

      const activeCellObj = cells.find((c) => c.id === activeCell);
      if (!activeCellObj) return;

      const isEnter = e.key === "Enter";
      const ctrlEnter = isEnter && (e.ctrlKey || e.metaKey);
      const shiftEnter = isEnter && e.shiftKey;

      if (ctrlEnter || shiftEnter) {
        if (!focusInsideNotebook) return;
        e.preventDefault();
        e.stopPropagation();
      }

      if (ctrlEnter) {
        if (activeCellObj.type === "code") {
          void executeCellRef.current(activeCell);
        } else if (activeCellObj.type === "markdown") {
          setEditingMarkdownCell(null);
        }
      } else if (shiftEnter) {
        if (activeCellObj.type === "code") {
          void executeCellRef.current(activeCell);
        } else if (activeCellObj.type === "markdown") {
          setEditingMarkdownCell(null);
        }

        const idx = cells.findIndex((c) => c.id === activeCell);
        if (idx >= 0 && idx < cells.length - 1) {
          const nextId = cells[idx + 1].id;
          setActiveCell(nextId);
          setTimeout(() => focusCell(nextId), 0);
        } else {
          const lastId = cells[cells.length - 1]?.id ?? null;
          addCell("code", lastId);
        }
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [activeCell, cells, editingMarkdownCell]);

  const isMarkdownEditing = (cell: Cell) =>
    cell.type === "markdown" && editingMarkdownCell === cell.id;

  return (
    <div
      ref={notebookShellRef}
      className="h-full flex flex-col overflow-hidden bg-white dark:bg-[#0d1117]"
    >
      {/* Title / kernel status */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-[#30363d] flex-shrink-0 bg-white dark:bg-[#161b22]">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            {isRenaming ? (
              <div className="flex items-center">
                <AutoSizeInput
                  type="text"
                  value={nameOnly}
                  onChange={(e) => setNameOnly(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={(e) => e.key === "Enter" && handleRename()}
                  className="font-medium text-gray-800 dark:text-[#f0f6fc] focus:outline-none bg-transparent"
                  autoFocus
                  disabled={isRenamingProp}
                />
                <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileExt}</span>
                {isRenamingProp && (
                  <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                )}
              </div>
            ) : (
              <>
                <h3 
                  className="font-medium text-gray-800 dark:text-[#f0f6fc] cursor-pointer hover:text-blue-600 dark:hover:text-[#f0883e] transition-colors select-none"
                  onDoubleClick={() => setIsRenaming(true)}
                  title="Double click to rename"
                >
                  {fileName}
                </h3>
                {isStudent && (

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsRenaming(true)}
                    className="dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
                  >
                    {isRenamingProp ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                    ) : (
                      <Edit className="text-gray-800 dark:text-[#c9d1d9]" size={14} />
                    )}
                  </Button>
                )}
              </>
            )}
            <span className="text-gray-400">•</span>
            <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              Python 3 {kernelStatus}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isStudent && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => addCell("code", activeCell)}
                className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
              >
                <Plus className="mr-2 h-4 w-4" /> Code
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => addCell("markdown", activeCell)}
                className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
              >
                <Plus className="mr-2 h-4 w-4" /> Markdown
              </Button>

              <div className="w-px bg-gray-200 dark:bg-[#30363d] h-6 mx-2" />
            </>
          )}

          {(() => {
            const navIdx = activeCell
              ? cells.findIndex((c) => c.id === activeCell)
              : -1;
            const canPrev = navIdx > 0;
            const canNext =
              navIdx >= 0 && navIdx < cells.length - 1;
            return (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => goToAdjacentCell(-1)}
                  disabled={!canPrev}
                  title="Previous cell (Alt+↑)"
                  className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d] px-2"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => goToAdjacentCell(1)}
                  disabled={!canNext}
                  title="Next cell (Alt+↓)"
                  className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d] px-2"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <div className="w-px bg-gray-200 dark:bg-[#30363d] h-6 mx-2" />
              </>
            );
          })()}

          <Button 
            size="sm" 
            variant="outline" 
            onClick={executeAllCells}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            <Play className="mr-2 h-4 w-4" /> Run All
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => void interruptKernel()}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            <Square className="mr-2 h-4 w-4" /> Stop
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={restartKernel}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Restart
          </Button>
        </div>
      </div>

      {/* Cells */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 dark:bg-[#0d1117]">
        {cells.map((cell: Cell, i: number) => {
          const isFirst = i === 0;
          const isLast = i === cells.length - 1;
          const showOutput = cell.ui?.showOutput ?? true;
          const isActive = activeCell === cell.id;

          return (
            <div
              key={cell.id}
              ref={(el) => (containerRefs.current[cell.id] = el)}
              tabIndex={-1}
              onClick={() => {
                setActiveCell(cell.id);
                if (editingMarkdownCell !== cell.id) {
                  setEditingMarkdownCell(null);
                }
              }}
              onDoubleClick={() => {
                if (cell.type === "markdown" && isStudent) {
                  setEditingMarkdownCell(cell.id);
                }
              }}
              className={`rounded-md border bg-gray-50 dark:bg-[#161b22] transition-all ${
                isActive 
                  ? "border-blue-200 dark:border-[#f0883e]" 
                  : "border-gray-200 dark:border-[#30363d]"
              }`}
            >
              <div
                className={`flex items-center justify-between px-3 py-1 border-b rounded-t-md text-xs ${
                  isActive
                    ? "bg-blue-500 dark:bg-[#f0883e] border-blue-200 dark:border-[#f0883e] text-white"
                    : "bg-blue-50 dark:bg-[#30363d] border-blue-100 dark:border-[#30363d] text-black dark:text-[#c9d1d9]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {cell.type === "code" ? "Code" : "Markdown"}
                  </span>

                  {cell.execution_count != null && (
                    <span className={cn("text-gray-400", isActive && "text-white/70")}>
                      [{cell.execution_count}]
                    </span>
                  )}

                  {(() => {
                    const status = executionStatuses[cell.id];
                    if (status === "Executing")
                      return (
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-normal pl-0 flex items-center"
                        >
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Executing...
                        </Badge>
                      );
                    if (status === "Executed")
                      return (
                        <Badge
                          variant="secondary"
                          className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 font-normal pl-0"
                        >
                          <Check className="h-4 w-4" />
                          Executed
                        </Badge>
                      );
                    return null;
                  })()}
                </div>

                {isStudent ? (
                  <div className="flex items-center gap-1">
                    {cell.type === "code" && (
                      <button
                        className={cn(
                          "p-1 rounded transition-colors",
                          isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                        )}
                        onClick={() => executeCell(cell.id)}
                        disabled={kernelStatus === "busy"}
                        title="Run (Ctrl+Enter)"
                      >
                        <Play size={14} />
                      </button>
                    )}

                    <button
                      className={cn(
                        "p-1 rounded disabled:opacity-40 transition-colors",
                        isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                      )}
                      onClick={() => moveCellUp(cell.id)}
                      disabled={isFirst}
                      title="Move up"
                    >
                      <ArrowUp size={14} />
                    </button>

                    <button
                      className={cn(
                        "p-1 rounded disabled:opacity-40 transition-colors",
                        isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                      )}
                      onClick={() => moveCellDown(cell.id)}
                      disabled={isLast}
                      title="Move down"
                    >
                      <ArrowDown size={14} />
                    </button>

                    <button
                      className={cn(
                        "p-1 rounded transition-colors",
                        isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                      )}
                      onClick={() => {
                        const idx = cells.findIndex((c) => c.id === cell.id);
                        if (idx <= 0) addCell("code", null, 0);
                        else addCell("code", null, idx);
                      }}
                      title="Add cell above"
                    >
                      <div className="flex flex-col items-center leading-none">
                        <Plus size={12} />
                        <div className={cn("w-3 h-0.5 mt-0.5 rounded-sm", isActive ? "bg-white" : "bg-gray-700 dark:bg-gray-400")} />
                      </div>
                    </button>

                    <button
                      className={cn(
                        "p-1 rounded transition-colors",
                        isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                      )}
                      onClick={() => {
                        const idx = cells.findIndex((c) => c.id === cell.id);
                        const insertAt = idx === -1 ? cells.length : idx + 1;
                        addCell("code", null, insertAt);
                      }}
                      title="Add cell below"
                    >
                      <div className="flex flex-col items-center leading-none">
                        <div className={cn("w-3 h-0.5 mb-0.5 rounded-sm", isActive ? "bg-white" : "bg-gray-700 dark:bg-gray-400")} />
                        <Plus size={12} />
                      </div>
                    </button>

                    {cell.type === "code" && (
                      <button
                        className={cn(
                          "p-1 rounded transition-colors",
                          isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                        )}
                        onClick={() => toggleOutput(cell.id)}
                        title={showOutput ? "Hide output" : "Show output"}
                      >
                        {showOutput ? (
                          <PanelBottomOpen size={14} />
                        ) : (
                          <PanelBottomClose size={14} />
                        )}
                      </button>
                    )}

                    <button
                      className={cn(
                        "p-1 rounded transition-colors",
                        isActive ? "hover:bg-red-400/20 text-white" : "hover:bg-blue-100 dark:hover:bg-[#21262d] text-red-500"
                      )}
                      onClick={() => deleteCell(cell.id)}
                      title="Delete cell"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    {cell.type === "code" && (
                      <button
                        className={cn(
                          "p-1 rounded transition-colors",
                          isActive ? "hover:bg-white/20" : "hover:bg-blue-100 dark:hover:bg-[#21262d]"
                        )}
                        onClick={() => executeCell(cell.id)}
                        disabled={kernelStatus === "busy"}
                        title="Run (Ctrl+Enter)"
                      >
                        <Play size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="p-3">
                {cell.type === "code" ? (
                  <CodeMirror
                    value={cell.content}
                    height="auto"
                    minHeight="20px"
                    extensions={[python(), EditorView.lineWrapping, selectionHighlight]}
                    theme={isDarkMode ? oneDark : githubLight}
                    onChange={(value) => updateCell(cell.id, value)}
                    onFocus={() => setActiveCell(cell.id)}
                    readOnly={!isStudent}
                    basicSetup={{
                      lineNumbers: true,
                      highlightActiveLineGutter: true,
                      highlightActiveLine: true,
                      foldGutter: true,
                      dropCursor: true,
                      indentOnInput: true,
                      syntaxHighlighting: true,
                      bracketMatching: true,
                      closeBrackets: true,
                      autocompletion: true,
                      rectangularSelection: true,
                      highlightSelectionMatches: true,
                    }}
                    className="text-sm border-none focus:outline-none focus:ring-0"
                    style={{
                      fontSize: "14px",
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    }}
                  />
                ) : (
                  <MarkdownCell
                    id={cell.id}
                    value={cell.content}
                    editing={isMarkdownEditing(cell)}
                    readOnly={!isStudent}
                    onFocus={() => setActiveCell(cell.id)}
                    onChange={(next) => updateCell(cell.id, next)}
                    isDarkMode={isDarkMode}
                  />
                )}

                {cell.type === "code" &&
                  showOutput &&
                  Array.isArray(cell.output) &&
                  cell.output.length > 0 && (
                    <div className="mt-3 border-t border-blue-100 dark:border-[#30363d] pt-3">
                      <div className="flex items-center">
                        <div className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                          Output{" "}
                          {cell.execution_count !== null
                            ? `[${cell.execution_count}]`
                            : ""}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            className="p-1 hover:bg-blue-100 dark:hover:bg-[#21262d] rounded text-red-600 dark:text-red-500"
                            onClick={() => clearOutput(cell.id)}
                            title="Clear output"
                          >
                            <Trash2 className="ml-2" size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="mt-2 rounded-md border border-[#F1E6C8] dark:border-yellow-900/30 bg-[#FDFBED] dark:bg-yellow-900/10">
                        <div className="p-3 font-normal text-[#d47f10] dark:text-[#f0883e]">
                          {renderCellOutputItemsUnified(cell.output)}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          );
        })}
      </div>

      {/* {error && (
        <div className="px-4 pb-4">
          <div className="text-sm text-red-600">{error}</div>
          {toast.error(error)}
        </div>
      )} */}
    </div>
  );
};

const Workspace = forwardRef(
  (
    {
      initialFile,
      onStateChange,
      onShare,
      onSave,
      onUpload,
      onSubmit,
      showOnlyExplorer,
      showOnlyContent,
      selectedFile,
      onFileSelect,
      isFileContentLoading,
      setIsFileContentLoading,
      pendingPath,
      setPendingPath,
      isDarkMode,
      notebookDraftsRef,
      stashNotebookBeforeNavigateRef,
    }: any,
    ref,
  ) => {
    const navigate = useNavigate();
    const {
      workspace,
      isWorkspaceReady,
      updateActivity,
      updateWorkspace,
      isConnectionVerified,
      validateSession,
    } = useJupyterWorkspace();
    const location = useLocation();
    const queryClient = useQueryClient();
    const updateContentMutation = useUpdateContentMutation();
    const executeCellMutation = useExecuteCellMutation();
    const executeNotebookMutation = useExecuteNotebookMutation();
    const renameContentMutation = useRenameContentMutation();
    const deleteContentMutation = useDeleteContentMutation();
    const uploadLTIFileMutation = useUploadJupyterFileMutation();
    const { projectId } = useParams();
    // const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
    const [editableFileContent, setEditableFileContent] = useState<string>("");
    const [notebookCells, setNotebookCells] = useState<Cell[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const notebookCellsRef = useRef<Cell[]>([]);
    const isDirtyRef = useRef(false);
    const selectedFileRef = useRef<FileItem | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [kernelStatus, setKernelStatus] = useState<"idle" | "busy" | "starting">("idle");
    const [recentlyDeletedPath, setRecentlyDeletedPath] = useState<
      string | null
    >(null);
    const [directory, setDirectory] = useState<string>("");
    const [collapseAllKey, setCollapseAllKey] = useState(0);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    // When a dataset file is still being downloaded in the background (local agent mode),
    // the file on disk contains an S3 pointer or placeholder. We track that state here
    // so we can show a friendly "downloading" UI and auto-retry.
    const [isDatasetDownloading, setIsDatasetDownloading] = useState(false);
    const datasetPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [expandedDirectories, setExpandedDirectories] = useState<
      Record<string, FileItem[]>
    >({});
    const { user } = useAuth();
    const isStudent = user?.role === ROLES.STUDENT;

    useEffect(() => {
      notebookCellsRef.current = notebookCells;
    }, [notebookCells]);
    useEffect(() => {
      isDirtyRef.current = isDirty;
    }, [isDirty]);
    useEffect(() => {
      selectedFileRef.current = selectedFile;
    }, [selectedFile]);

    useLayoutEffect(() => {
      if (!showOnlyContent || !stashNotebookBeforeNavigateRef) return;
      stashNotebookBeforeNavigateRef.current = (nextPath: string) => {
        const prev = selectedFileRef.current;
        if (
          prev?.path &&
          prev.path !== nextPath &&
          isDirtyRef.current &&
          fileItemIsNotebook(prev)
        ) {
          notebookDraftsRef.current[prev.path] = {
            dirty: true,
            cells: cloneNotebookCellsForDraft(notebookCellsRef.current),
          };
        }
      };
      return () => {
        stashNotebookBeforeNavigateRef.current = null;
      };
    }, [showOnlyContent, notebookDraftsRef, stashNotebookBeforeNavigateRef]);

    const {
      data: filesData,
      isLoading: isLoadingFiles,
      isError: isFilesError,
      error: filesError,
      refetch: refetchFiles,
    } = useQuery({
      queryKey: ["files", projectId],
      queryFn: async () => {
        if (!projectId) {
          throw new Error("projectId is missing");
        }
        const defaultFolderPath =
          workspace.workspaceType === "local" ? "/" : projectId;
        const targetPath = (workspace.folderPath || defaultFolderPath || "").replace(/^\//, "");
        const res = await getContents(targetPath, workspace.workspaceType === 'local');
        return res.content || [];
      },
      // Only fire once the workspace gateway URL is resolved (workspace.status becomes
      // 'active' only after resolveWorkspaceUrl + heartbeat complete in the context).
      // This prevents the query hitting the Vite dev-server (port 3000) instead of the
      // workspace service (port 3005) during the async initialization on page reload.
      enabled: Boolean(projectId) && workspace.status === "active" && isConnectionVerified,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    });

    useQuery({
      queryKey: ["files", projectId, directory],
      queryFn: async () => {
        if (!projectId || !directory) {
          throw new Error("projectId or directory is missing");
        }
        const res = await getContents(`${projectId}/${directory}`, workspace.workspaceType === 'local');
        return res.content || [];
      },
      enabled: false,
      refetchOnWindowFocus: false,
    });

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
    const files = React.useMemo(() => filesData ?? [], [filesData]);

    useEffect(() => {
      onStateChange({ isDirty, isSaving });
    }, [isDirty, isSaving, onStateChange]);

    // Sync URL projectId to global workspace context to ensure background heartbeats start
    useEffect(() => {
      if (workspace.status === "error" || workspace.status === "stopping") {
        return;
      }
      if (projectId && workspace.project?.id !== projectId) {
        console.log("NotebookPage: Syncing projectId to global context:", projectId);
        updateWorkspace({ 
          project: { id: projectId },
          // Force status to provisioning to ensure heartbeats have something to check
          status: workspace.status === "none" ? "provisioning" : workspace.status
        });
      }
    }, [projectId, workspace.project?.id, workspace.status, updateWorkspace]);

    // Handle initial navigation kernel-recovery race: if we navigated here but kernelId
    // is missing (or empty string from cloud polling), attempt one last recovery.
    useEffect(() => {
      if (workspace.status === "active" && !workspace.kernelId && projectId) {
        const recoverKernel = async () => {
          try {
            const sessions = await listSessions(projectId);
            const nbName = workspace.notebookPath?.split("/").pop();
            const match =
              sessions.find(
                (s: any) =>
                  nbName &&
                  (s.path === workspace.notebookPath ||
                    s.path?.endsWith(nbName)),
              ) || sessions.find((s: any) => s.path?.includes(projectId));
            if (match?.kernel?.id) {
              updateWorkspace({ kernelId: match.kernel.id });
            }
          } catch (e) {
            console.warn("NotebookPage: Failed auto-recovery of kernelId:", e);
          }
        };
        recoverKernel();
      }
    }, [
      workspace.status,
      workspace.kernelId,
      workspace.notebookPath,
      projectId,
      updateWorkspace,
    ]);

    const handleFileSelect = useCallback(
      async (fileInfo: { path: string; type: string }) => {
        if (!fileInfo.path || fileInfo.path === recentlyDeletedPath) return;

        if (fileInfo.type === "directory") {
          onFileSelect(null);
          return;
        }
        if (selectedFile?.path === fileInfo.path) {
          return;
        }

        stashNotebookBeforeNavigateRef?.current?.(fileInfo.path);

        // Clear any existing background-download poll for a previous file
        if (datasetPollRef.current) {
          clearTimeout(datasetPollRef.current);
          datasetPollRef.current = null;
        }
        setIsDatasetDownloading(false);

        setPendingPath(fileInfo.path);
        setIsFileContentLoading(true);
        try {
          const fileDetails = await getContents(fileInfo.path, workspace.workspaceType === 'local');
          if (!fileDetails.name) {
            onFileSelect(null);
            return;
          }

          // --- Dataset pointer / background-download detection ---
          // The init.sh writes a small placeholder into dataset files while they
          // are being downloaded asynchronously. Detect that and show a friendly
          // "still downloading" UI instead of garbled content.
          if (isDatasetPointerContent(fileDetails)) {
            onFileSelect(fileDetails as FileItem);
            setIsDatasetDownloading(true);
            setEditableFileContent("");

            // Auto-poll every 6 s until the real file arrives
            const schedulePoll = () => {
              datasetPollRef.current = setTimeout(async () => {
                try {
                  const fresh = await getContents(fileInfo.path, workspace.workspaceType === 'local');
                  if (!isDatasetPointerContent(fresh)) {
                    // Real content available — load it normally
                    setIsDatasetDownloading(false);
                    onFileSelect(fresh as FileItem);
                    if (fresh.type === "file") {
                      setEditableFileContent(fresh.content || "");
                    }
                    
                    // Manually update the file in expandedDirectories to clear the "Syncing..." badge immediately
                    const parentPath = fresh.path.split('/').slice(0, -1).join('/');
                    if (parentPath && expandedDirectories[parentPath]) {
                      setExpandedDirectories(prev => ({
                        ...prev,
                        [parentPath]: prev[parentPath].map(f => 
                          f.path === fresh.path ? { ...f, size: fresh.size ?? undefined, last_modified: fresh.last_modified } : f
                        )
                      }));
                    }

                    setRefreshTrigger((p) => p + 1);
                    // Bust all file-related caches
                    queryClient.invalidateQueries({ queryKey: ["files"] });
                  } else {
                    // Still downloading — keep polling
                    schedulePoll();
                  }
                } catch {
                  schedulePoll(); // network hiccup — retry
                }
              }, 6000);
            };
            schedulePoll();
            return;
          }
          // --- End pointer detection ---
          
          // If the file is real content, manually update the expandedDirectories state 
          // to clear any "Syncing..." badges in the Project Explorer tree immediately.
          const parentPath = fileDetails.path.split('/').slice(0, -1).join('/');
          if (parentPath && expandedDirectories[parentPath]) {
            setExpandedDirectories(prev => ({
              ...prev,
              [parentPath]: prev[parentPath].map(f => 
                f.path === fileDetails.path ? { ...f, size: fileDetails.size ?? undefined, last_modified: fileDetails.last_modified } : f
              )
            }));
          }

          onFileSelect(fileDetails as FileItem);

          setEditableFileContent("");

          const asFileItem = fileDetails as FileItem;
          const isNotebookFile =
            fileDetails.type === "notebook" || fileItemIsNotebook(asFileItem);

          if (isNotebookFile) {
            const draft = notebookDraftsRef.current[fileDetails.path];
            if (draft?.dirty) {
              setNotebookCells(draft.cells);
              setIsDirty(true);
            } else {
              const notebookContent = fileDetails.content;
              const cells = notebookContent?.cells || [];
              setNotebookCells(mapFetchedNotebookCells(cells));
              setIsDirty(false);
            }
          } else if (fileDetails.type === "file") {
            setNotebookCells([]);
            setIsDirty(false);
            const ext = fileDetails.name?.split(".").pop()?.toLowerCase();
            const isXl = ext === "xlsx" || ext === "xls";
            // If server returned a nested 'format' flag, prefer base64 path
            const fmt = (fileDetails as any).format;
            const cnt = fileDetails.content;
            if (isXl) {
              // If not already base64, convert text to base64
              const asBase64 =
                typeof cnt === "string" && fmt === "base64"
                  ? cnt
                  : typeof cnt === "string"
                    ? btoa(unescape(encodeURIComponent(cnt)))
                    : cnt;
              setEditableFileContent(asBase64);
            } else {
              setEditableFileContent(fileDetails.content || "");
            }
          }
        } catch (err: any) {
          toast.error("Could not load the selected file.");
          onFileSelect(null);
        } finally {
          setIsFileContentLoading(false);
          setPendingPath(null);
        }
      },
      [recentlyDeletedPath, selectedFile?.path, onFileSelect, workspace.workspaceType],
    );

    const hasAppliedLocationFile = useRef(false);
    const hasSelectedInitial = useRef(false);
    const lastLoadedFilePath = useRef<string | null>(null);

    // Effect to load file content when selectedFile changes (from parent/column 2)
    useEffect(() => {
      if (
        !selectedFile?.path ||
        selectedFile.path === lastLoadedFilePath.current
      ) {
        return;
      }

      lastLoadedFilePath.current = selectedFile.path;

      const loadFileContent = async () => {
        setIsFileContentLoading(true);
        try {
          const fileDetails = await getContents(selectedFile.path, workspace.workspaceType === 'local');

          setEditableFileContent("");

          const asFileItem = fileDetails as FileItem;
          const isNotebookFile =
            fileDetails.type === "notebook" || fileItemIsNotebook(asFileItem);

          if (isNotebookFile) {
            const draft = notebookDraftsRef.current[fileDetails.path];
            if (draft?.dirty) {
              setNotebookCells(draft.cells);
              setIsDirty(true);
            } else {
              const notebookContent = fileDetails.content;
              const cells = notebookContent?.cells || [];
              setNotebookCells(mapFetchedNotebookCells(cells));
              setIsDirty(false);
            }
          } else if (fileDetails.type === "file") {
            setNotebookCells([]);
            setIsDirty(false);
            const ext = fileDetails.name?.split(".").pop()?.toLowerCase();
            const isXl = ext === "xlsx" || ext === "xls";
            const fmt = (fileDetails as any).format;
            const cnt = fileDetails.content;
            if (isXl) {
              const asBase64 =
                typeof cnt === "string" && fmt === "base64"
                  ? cnt
                  : typeof cnt === "string"
                    ? btoa(unescape(encodeURIComponent(cnt)))
                    : cnt;
              setEditableFileContent(asBase64);
            } else {
              setEditableFileContent(fileDetails.content || "");
            }
          }
        } catch (err: any) {
          console.error("Failed to load file content:", err);
          toast.error("Could not load the selected file.");
        } finally {
          setIsFileContentLoading(false);
        }
      };

      loadFileContent();
    }, [selectedFile?.path]);

    useEffect(() => {
      const fileFromState = (location.state as any)?.file;

      // 1. Apply file from route state only once
      if (!hasAppliedLocationFile.current && fileFromState?.path) {
        hasAppliedLocationFile.current = true;
        handleFileSelect({
          path: fileFromState.path,
          type: fileFromState.type,
        });
        return;
      }

      // 2. If we already have a selected file, do nothing
      if (isLoadingFiles || selectedFile) return;

      // 3. Apply initialFile or notebookPath from workspace only once as a fallback
      const targetInitialPath = initialFile?.path || workspace?.notebookPath;
      const targetInitialType = initialFile?.path
        ? initialFile.type
        : workspace?.notebookPath?.endsWith(".ipynb")
          ? "notebook"
          : "file";

      if (
        targetInitialPath &&
        !recentlyDeletedPath &&
        !hasSelectedInitial.current
      ) {
        hasSelectedInitial.current = true;
        handleFileSelect({ path: targetInitialPath, type: targetInitialType });
        return;
      }

      // 4. Fallback to most recent file
      if (!files.length) {
        onFileSelect(null);
        setEditableFileContent("");
        setNotebookCells([]);
        return;
      }

      const sortedFiles = [...files].sort(sortFiles);

      const mostRecentNotebook = sortedFiles.find(
        (f) =>
          f.type !== "directory" &&
          f.path !== recentlyDeletedPath &&
          f.name &&
          (f.name.endsWith(".ipynb") || f.type === "notebook"),
      );
      const mostRecentFile =
        mostRecentNotebook ||
        sortedFiles.find(
          (f) =>
            f.type !== "directory" && f.path !== recentlyDeletedPath && f.name,
        );

      if (mostRecentFile) {
        handleFileSelect({
          path: mostRecentFile.path,
          type: mostRecentFile.type,
        });
      } else {
        onFileSelect(null);
        setEditableFileContent("");
        setNotebookCells([]);
      }
    }, [
      files,
      isLoadingFiles,
      initialFile,
      workspace?.notebookPath,
      recentlyDeletedPath,
      handleFileSelect,
      location.state,
      location.pathname,
    ]);

    const handleContentChange = (newContent: string) => {
      setEditableFileContent(newContent);
      setIsDirty(true);
    };

    const handleNotebookCellsChange = (value: SetStateAction<Cell[]>) => {
      setNotebookCells(value);
      setIsDirty(true);
    };

    const handleFileCreate = async (
      fileType: "notebook" | "python" | "markdown" | "text",
      fileName: string,
    ) => {
      try {
        const defaultFolderPath =
          workspace.workspaceType === "local" ? "/" : `/${projectId}`;
        const folderPath = (workspace.folderPath || defaultFolderPath || "/").replace(/\/+$/, "");
        const filePath = folderPath ? `${folderPath}/${fileName}` : fileName;
        let content: any, type: string, format: string;

        if (fileType === "notebook") {
          type = "notebook";
          format = "json";
          content = {
            cells: [
              {
                cell_type: "code",
                execution_count: null,
                metadata: {},
                outputs: [],
                source: [""],
              },
            ],
            metadata: {
              kernelspec: {
                display_name: "Python 3",
                language: "python",
                name: "python3",
              },
              language_info: { name: "python", version: "3.9.7" },
            },
            nbformat: 4,
            nbformat_minor: 5,
          };
        } else {
          type = "file";
          format = "text";
          content =
            fileType === "python"
              ? "# Python script\n"
              : fileType === "markdown"
                ? "# Markdown File\n\nYour content here..."
                : "";
        }

        await updateContentMutation.mutateAsync({
          path: filePath,
          content,
          type,
          format,
        });
        queryClient.setQueryData<FileItem[]>(["files", projectId], (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((f) => f.path === filePath)) return list;
          const nowIso = new Date().toISOString();
          const newItem: FileItem = {
            name: fileName,
            path: filePath,
            type: type === "notebook" ? "notebook" : "file",
            mimetype:
              type === "notebook" ? "application/x-ipynb+json" : undefined,
            size: (content as any)?.length ?? 0,
            last_modified: nowIso,
            content: undefined,
          };
          return [...list, newItem];
        });
        await handleFileSelect({
          path: filePath,
          type: fileType === "notebook" ? "notebook" : "file",
        });
        updateActivity();
      } catch (err: any) {
        toast.error(
          `Failed to create ${fileType} file: ${err.message || "Unknown error"}`,
        );
      }
    };

    const handleSaveNow = async () => {
      if (!selectedFile || !isDirty || !projectId || isSaving || !isStudent || kernelStatus === "busy")
        return;
      if (!navigator.onLine || document.hidden) return;
      const saveToastId = "workspace-file-save";
      toast.loading("Saving...", { id: saveToastId });
      setIsSaving(true);
      try {
        if (selectedFile.type === "notebook") {
          const notebookContentToSave = {
            cells: notebookCells.map((cell) => ({
              cell_type: cell.type,
              metadata: cell.metadata || {},
              source: Array.isArray(cell.content)
                ? cell.content
                : [cell.content],
              ...(cell.type === "code" && {
                execution_count: cell.execution_count,
                outputs: cell.output || [],
              }),
            })),
            metadata: {
              kernelspec: {
                display_name: "Python 3",
                language: "python",
                name: "python3",
              },
              language_info: { name: "python", version: "3.9.7" },
              ...(selectedFile as any).content?.metadata,
            },
            nbformat: 4,
            nbformat_minor: 5,
          };
          await updateContentMutation.mutateAsync({
            path: selectedFile.path,
            content: notebookContentToSave,
            type: "notebook",
            format: "json",
          });
        } else if (selectedFile.type === "file") {
          await updateContentMutation.mutateAsync({
            path: selectedFile.path,
            content: editableFileContent,
            type: "file",
            format: "text",
          });
        }
        setIsDirty(false);
        if (selectedFile.type === "notebook") {
          delete notebookDraftsRef.current[selectedFile.path];
        }
        toast.dismiss(saveToastId);
        toast.success("File saved successfully!");
      } catch (err: any) {
        toast.dismiss(saveToastId);
        toast.error(`Failed to save file: ${err.message || "Unknown error"}`);
      } finally {
        setIsSaving(false);
      }
    };

    useEffect(() => {
      if (!isDirty || isSaving || isRenaming || isDeleting || kernelStatus === "busy") return;
      if (!navigator.onLine || document.hidden) return;
      const idleTimeout = setTimeout(() => {
        handleSaveNow();
      }, 60000);
      return () => {
        clearTimeout(idleTimeout);
      };
    }, [isDirty, editableFileContent, notebookCells, isSaving, kernelStatus]);

    // Cleanup: clear dataset-download poll timer on unmount
    useEffect(() => {
      return () => {
        if (datasetPollRef.current) {
          clearTimeout(datasetPollRef.current);
        }
      };
    }, []);

    const handleUpload = () => {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = SUPPORTED_FILE_TYPES.map((ext) => `.${ext}`).join(",");
      input.onchange = async (e) => {
        const list = (e.target as HTMLInputElement).files;
        const selected = list ? Array.from(list) : [];
        if (!selected.length || !projectId) return;
        if (selected.length > 20) {
          toast.error(`You can upload at most 20 files at a time.`);
          return;
        }
        const tooLarge = selected.filter((f) => f.size > MAX_FILE_SIZE);
        if (tooLarge.length) {
          toast.error(
            `Some files exceed the 50MB limit: ${tooLarge
              .map((f) => `${f.name} (${formatFileSize(f.size)})`)
              .join(", ")}.`,
          );
          return;
        }
        const invalid = selected.filter((f) => {
          const ext = f.name.split(".").pop()?.toLowerCase();
          return !ext || !SUPPORTED_FILE_TYPES.includes(ext);
        });
        if (invalid.length) {
          toast.error(
            `Unsupported file types: ${invalid.map((f) => f.name).join(", ")}.`,
          );
          return;
        }
        const existingNames = new Set(files.map((x: FileItem) => x.name));
        const dupExisting = selected.filter((f) => existingNames.has(f.name));
        if (dupExisting.length) {
          toast.error(
            `Already exists: ${dupExisting
              .map((f) => `"${f.name}"`)
              .join(", ")}. Please rename.`,
          );
          return;
        }
        const nameCounts = new Map<string, number>();
        const dupInSelection = selected.filter((f) => {
          nameCounts.set(f.name, (nameCounts.get(f.name) ?? 0) + 1);
          return nameCounts.get(f.name)! > 1;
        });
        if (dupInSelection.length) {
          toast.error(
            `Duplicate names in selection: ${[
              ...new Set(dupInSelection.map((f) => f.name)),
            ].join(", ")}.`,
          );
          return;
        }

        const loadingToast = toast.loading(
          `Uploading ${selected.length} file(s)...`,
        );
        try {
          await uploadLTIFileMutation.mutateAsync({
            files: selected,
            projectId,
            username: user?.id ? `user_${user.id}` : undefined,
            isLocal: workspace.workspaceType === "local",
          });

          toast.dismiss(loadingToast);
          toast.success(`Uploaded ${selected.length} file(s) successfully.`);
          updateActivity();
          setRefreshTrigger((prev) => prev + 1);
        } catch (error: any) {
          toast.dismiss(loadingToast);
          if (error.response?.status === 413) {
            toast.error("File too large. Please use a file smaller than 50MB.");
          } else if (error.response?.status === 415) {
            toast.error(
              "Unsupported file type. Please try a different format.",
            );
          } else if (error.response?.data?.data?.message) {
            toast.error(
              `Upload failed: ${error.response?.data?.data?.message}`,
            );
          } else {
            toast.error(
              `Upload failed: ${
                error.response?.data?.message || "An unknown error occurred."
              }`,
            );
          }
        }
      };
      input.click();
    };

    const handleRename = async (newName: string) => {
      if (
        !selectedFile ||
        !newName ||
        newName === selectedFile.name ||
        isRenaming ||
        isSaving
      )
        return;
      const fileExists = files.some(
        (f: FileItem) => f.name === newName && f.path !== selectedFile.path,
      );

      if (fileExists) {
        toast.error(`A file named "${newName}" already exists.`);
        throw new Error("File already exists");
      }

      setIsRenaming(true);
      const loadingToast = toast.loading(`Renaming to "${newName}"...`);
      try {
        const oldPath = selectedFile.path;
        const pathParts = oldPath?.split("/");
        pathParts[pathParts.length - 1] = newName;
        const newPath = pathParts.join("/");
        
        await renameContentMutation.mutateAsync({ oldPath, newPath });
        toast.dismiss(loadingToast);
        toast.success(`File renamed to "${newName}" successfully!`);
        
        // Update local file list cache immediately for better UX
        queryClient.setQueryData(["files", projectId], (oldFiles: FileItem[] | undefined) => {
          if (!oldFiles) return [];
          return oldFiles.map(f => f.path === oldPath ? { ...f, name: newName, path: newPath } : f);
        });
        
        // Manually update the selected file state with new path and name
        const renamedFile: FileItem = {
          ...selectedFile,
          name: newName,
          path: newPath,
        };
        onFileSelect?.(renamedFile);

        if (notebookDraftsRef.current[oldPath]) {
          notebookDraftsRef.current[newPath] = notebookDraftsRef.current[oldPath];
          delete notebookDraftsRef.current[oldPath];
        }

        // Finally invalidate to ensure sync with server
        queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      } catch (err: any) {
        toast.dismiss(loadingToast);
        if (err?.response?.status === 409)
          toast.error(`A file named "${newName}" already exists.`);
        else if (err?.response?.status === 404) toast.error("File not found.");
        else
          toast.error(
            `Failed to rename file: ${
              err.response?.data?.data?.message ||
              err?.response?.data?.message ||
              err?.message ||
              "Unknown error"
            }`,
          );
        throw err;
      } finally {
        setIsRenaming(false);
      }
    };

    const handleDelete = async (filePath: string) => {
      if (!filePath || isDeleting) return;
      if (isSaving) {
        toast.error(
          "Please wait until the save is complete before deleting the file",
        );
        return;
      }
      const isConfirmed = window.confirm(
        `Are you sure you want to delete this file? This action cannot be undone.`,
      );
      if (!isConfirmed) return;

      setIsDeleting(true);
      try {
        delete notebookDraftsRef.current[filePath];
        if (selectedFile?.path === filePath) {
          onFileSelect?.(null);
        }
        setRecentlyDeletedPath(filePath);
        await deleteContentMutation.mutateAsync(filePath);
        toast.success("File deleted successfully!");
        updateActivity();
        queryClient.invalidateQueries({ queryKey: ["files", projectId] });
        setRefreshTrigger((prev) => prev + 1);
      } catch (err: any) {
        toast.error(
          `Failed to delete file: ${
            err.response?.data?.data?.message || err?.message || "Unknown error"
          }`,
        );
        setRecentlyDeletedPath(null);
      } finally {
        setIsDeleting(false);
      }
    };

    useImperativeHandle(ref, () => ({
      save: handleSaveNow,
      upload: handleUpload,
      getFiles: () => files,
    }));

    const toggleSidebar = () => {
      setIsSidebarCollapsed((prev) => !prev);
    };

    const isNotebook = (file: FileItem | null): file is NotebookFile => {
      if (!file?.name) return false;
      return (
        file.type === "notebook" ||
        file.name.endsWith(".ipynb") ||
        file.mimetype === "application/x-ipynb+json"
      );
    };

    if (isLoadingFiles) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-gray-500" />
        </div>
      );
    }

    const renderContent = () => {
      if (isFileContentLoading) {
        const fileName = pendingPath?.split("/").pop() || "file";
        return (
          <div className="flex flex-col h-full bg-white dark:bg-[#0d1117]">
            <div className="flex-shrink-0 flex items-center px-6 py-3 border-b bg-white dark:bg-[#161b22] dark:border-[#30363d]">
              <h3 className="font-medium text-gray-400 dark:text-[#8b949e] animate-pulse">
                Loading {fileName}...
              </h3>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-500 dark:text-[#f0883e] mb-4" />
                <p className="text-sm text-gray-500 dark:text-[#8b949e]">Fetching file contents...</p>
              </div>
            </div>
          </div>
        );
      }

      // Dataset background-download state: show a friendly waiting UI instead of
      // rendering garbled S3 pointer / placeholder text in the CSVEditor.
      if (isDatasetDownloading && selectedFile) {
        const fileName = selectedFile.name;
        return (
          <div className="flex flex-col h-full bg-white dark:bg-[#0d1117]">
            {/* Header */}
            <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3 border-b bg-white dark:bg-[#161b22] dark:border-[#30363d]">
              <Database size={16} className="text-blue-500 dark:text-[#58a6ff] flex-shrink-0" />
              <h3 className="font-medium text-gray-800 dark:text-[#f0f6fc]">{fileName}</h3>
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                <Loader2 size={10} className="animate-spin" />
                Downloading
              </span>
            </div>
            {/* Body */}
            <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6">
              {/* Animated progress bar */}
              <div className="w-72 max-w-full">
                <div className="h-1.5 w-full bg-gray-200 dark:bg-[#30363d] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 dark:from-[#388bfd] dark:to-[#58a6ff] rounded-full"
                    style={{
                      width: "60%",
                      animation: "indeterminate-progress 1.8s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <Database size={36} className="text-blue-400 dark:text-[#388bfd] mb-1" />
                <p className="text-base font-semibold text-gray-800 dark:text-[#e6edf3]">
                  Dataset is downloading
                </p>
                <p className="text-sm text-gray-500 dark:text-[#8b949e] max-w-xs">
                  <span className="font-medium text-gray-700 dark:text-[#c9d1d9]">{fileName}</span> is
                  being synced to your sandbox in the background.
                  This page will refresh automatically once it is ready.
                </p>
                <p className="text-xs text-gray-400 dark:text-[#6e7681] mt-1">
                  Checking every 6 seconds…
                </p>
              </div>
            </div>
            <style>{`
              @keyframes indeterminate-progress {
                0%   { transform: translateX(-100%); width: 60%; }
                50%  { transform: translateX(66%);   width: 60%; }
                100% { transform: translateX(250%);  width: 60%; }
              }
            `}</style>
          </div>
        );
      }

      if (!selectedFile) {
        return (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-[#8b949e]">
            <p>Select a file to begin.</p>
          </div>
        );
      }

      if (isNotebook(selectedFile)) {
        return (
          <NotebookContent
            notebook={selectedFile as Notebook}
            onRename={handleRename}
            cells={notebookCells}
            onCellsChange={handleNotebookCellsChange}
            workspace={workspace}
            isWorkspaceReady={isWorkspaceReady()}
            updateActivity={updateActivity}
            executeCellMutation={executeCellMutation}
            executeNotebookMutation={executeNotebookMutation}
            executionRecovery={{
              projectId,
              updateWorkspace,
              validateSession,
            }}
            isRenaming={isRenaming}
            isStudent={isStudent}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleSidebar={toggleSidebar}
            onShare={onShare}
            onSave={onSave}
            onUpload={onUpload}
            onSubmit={onSubmit}
            isDirty={isDirty}
            isSaving={isSaving}
            onKernelStatusChange={setKernelStatus}
            isDarkMode={isDarkMode}
          />
        );
      }

      if (isCsvFile(selectedFile)) {
        return (
          <CSVEditor
            file={selectedFile}
            content={editableFileContent}
            onRename={handleRename}
            isRenaming={isRenaming}
            isStudent={isStudent}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleSidebar={toggleSidebar}
            isDarkMode={isDarkMode}
          />
        );
      }
      if (isXlFile(selectedFile)) {
        return (
          <ExcelEditor
            file={selectedFile}
            content={editableFileContent}
            onRename={handleRename}
            isRenaming={isRenaming}
            isStudent={isStudent}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleSidebar={toggleSidebar}
            isDarkMode={isDarkMode}
          />
        );
      }
      if (isEditableFile(selectedFile)) {
        return (
          <EditableFileViewer
            file={selectedFile}
            content={editableFileContent}
            onContentChange={handleContentChange}
            onRename={handleRename}
            isRenaming={isRenaming}
            isStudent={isStudent}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleSidebar={toggleSidebar}
            isDarkMode={isDarkMode}
          />
        );
      }

      return (
        <ViewOnlyViewer
          file={selectedFile}
          onRename={handleRename}
          isRenaming={isRenaming}
          isStudent={isStudent}
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
          isDarkMode={isDarkMode}
        />
      );
    };

    // Render only explorer for middle column
    if (showOnlyExplorer) {
      return (
        <div className="flex flex-col h-full w-full bg-white dark:bg-[#161b22]">
          <div className="flex-1 overflow-y-auto">
            <ProjectExplorer
              files={files}
              isLoadingFiles={isLoadingFiles}
              onFileSelect={handleFileSelect}
              onFileCreate={handleFileCreate}
              onFileDelete={handleDelete}
              selectedFilePath={selectedFile?.path ?? null}
              isDeleting={isDeleting}
              initialFile={initialFile}
              isStudent={isStudent}
              recentlyDeletedPath={recentlyDeletedPath}
              onCollapseAll={collapseAllKey}
              isDarkMode={isDarkMode}
              onRefresh={refetchFiles}
              refreshTrigger={refreshTrigger}
              expandedDirectories={expandedDirectories}
              setExpandedDirectories={setExpandedDirectories}
            />
          </div>
        </div>
      );
    }

    // Render only content for right column
    if (showOnlyContent) {
      return (
        <div className="flex-1 h-full flex flex-col overflow-hidden pt-4 bg-white dark:bg-[#0d1117]">
          {renderContent()}
        </div>
      );
    }

    return (
      <div className="flex h-full bg-gray-100 dark:bg-[#0d1117]">
        {/* Collapsible Sidebar */}
        <div
          className={cn(
            "flex-shrink-0 h-full flex flex-col border-r transition-all duration-300 ease-in-out overflow-hidden dark:border-[#30363d] dark:bg-[#161b22]",
            isSidebarCollapsed ? "w-0" : "w-80",
          )}
        >
          {!isSidebarCollapsed && (
            <>
              <ProjectExplorer
                files={files}
                isLoadingFiles={isLoadingFiles}
                onFileSelect={handleFileSelect}
                onFileCreate={handleFileCreate}
                onFileDelete={handleDelete}
                selectedFilePath={selectedFile?.path ?? null}
                isDeleting={isDeleting}
                initialFile={initialFile}
                isStudent={isStudent}
                recentlyDeletedPath={recentlyDeletedPath}
                onCollapseAll={collapseAllKey}
                isDarkMode={isDarkMode}
                onRefresh={refetchFiles}
                refreshTrigger={refreshTrigger}
              />
            </>
          )}
        </div>

        <main className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-[#0d1117]">
          {renderContent()}
        </main>
      </div>
    );
  },
);

// --- MAIN PAGE COMPONENT ---
export default function NotebookPage({ project }: { project?: any }) {
  const { workspace, updateActivity } = useJupyterWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  /** Shared across the two Workspace mounts (explorer vs editor) so unsaved notebook cells persist when switching files from the sidebar. */
  const notebookDraftsRef = useRef<
    Record<string, { dirty: boolean; cells: Cell[] }>
  >({});
  const stashNotebookBeforeNavigateRef = useRef<
    ((nextPath: string) => void) | null
  >(null);
  const [isFileContentLoading, setIsFileContentLoading] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [secondColumnView, setSecondColumnView] = useState<
    "explorer" | "messages"
  >("explorer");

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("sandbox-theme");
    return saved === "dark";
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [isDarkMode]);

  const lastActivityBumpAtRef = useRef(0);
  useEffect(() => {
    const THROTTLE_MS = 8000;
    const bump = () => {
      const t = Date.now();
      if (t - lastActivityBumpAtRef.current < THROTTLE_MS) return;
      lastActivityBumpAtRef.current = t;
      updateActivity();
    };
    bump();
    window.addEventListener("keydown", bump);
    window.addEventListener("click", bump);
    window.addEventListener("wheel", bump, { passive: true });
    window.addEventListener("mousemove", bump, { passive: true });
    const onVisibility = () => {
      if (!document.hidden) bump();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("keydown", bump);
      window.removeEventListener("click", bump);
      window.removeEventListener("wheel", bump);
      window.removeEventListener("mousemove", bump);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [updateActivity]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem("sandbox-theme", newValue ? "dark" : "light");
      return newValue;
    });
  };

  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((mouseDownEvent: React.MouseEvent) => {
    setIsResizing(true);
    // Prevent text selection during resizing
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing) {
        // Calculate new width: mouse position minus width of first icon sidebar (64px)
        const newWidth = mouseMoveEvent.clientX - 64;

        if (newWidth < 150) {
          // Snap to collapse if dragged too small
          setSidebarWidth(0);
        } else if (newWidth > 600) {
          // Limit maximum width
          setSidebarWidth(600);
        } else {
          setSidebarWidth(newWidth);
        }
      }
    },
    [isResizing],
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [aiSidebarWidth, setAiSidebarWidth] = useState(350);
  const [isAIResizing, setIsAIResizing] = useState(false);

  const startAIResizing = useCallback((mouseDownEvent: React.MouseEvent) => {
    setIsAIResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }, []);

  const stopAIResizing = useCallback(() => {
    setIsAIResizing(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const resizeAI = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isAIResizing) {
        const newWidth = window.innerWidth - mouseMoveEvent.clientX;
        if (newWidth < 200) {
          setIsAIChatOpen(false);
        } else if (newWidth > 800) {
          setAiSidebarWidth(800);
        } else {
          setAiSidebarWidth(newWidth);
        }
      }
    },
    [isAIResizing],
  );

  useEffect(() => {
    if (isAIResizing) {
      window.addEventListener("mousemove", resizeAI);
      window.addEventListener("mouseup", stopAIResizing);
    }
    return () => {
      window.removeEventListener("mousemove", resizeAI);
      window.removeEventListener("mouseup", stopAIResizing);
    };
  }, [isAIResizing, resizeAI, stopAIResizing]);

  const workspaceRef = useRef<any>(null);
  /** Session-lost toast: only after we had a sessionId, then lost it (not on reload hydration). */
  const prevWorkspaceSessionIdRef = useRef<string | null | undefined>(undefined);
  const sessionLostToastReadyRef = useRef(false);
  const activeProject =
    project || workspace.project || (location.state as any)?.project;
  const [authFailed, setAuthFailed] = useState(false);
  const [toolbarState, setToolbarState] = useState({
    isDirty: false,
    isSaving: false,
  });

  const { user } = useAuth();
  const isStudent = user?.role === ROLES.STUDENT;
  
  useEffect(() => {
    if (location.state?.fromDetails) {
      const returnTab = isStudent ? "checkpoints" : "submissions";
      sessionStorage.setItem("project_return_tab", returnTab);
    }
  }, [isStudent, location.state?.fromDetails]);
  // Practice projects don't need submission - they're for self-paced learning
  const isPracticeProject =
    activeProject?.isPracticeProject || workspace.isPracticeProject || false;
  const { setSidebarVisible } = useNavigation();

  const handleWorkspaceStateChange = useCallback((newState: any) => {
    setToolbarState(newState);
  }, []);

  const handleSaveNow = () => workspaceRef.current?.save();

  const handleUpload = () => workspaceRef.current?.upload();

  const [isSubmitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [isFileSharingOpen, setFileSharingOpen] = useState(false);

  const handleFileSelect = (file: FileItem | null) => {
    setSelectedFile(file);
  };

  const handleOpenSubmitDialog = () => {
    setSubmitDialogOpen(true);
  };
  const handleCloseSubmitDialog = () => {
    setSubmitDialogOpen(false);
  };

  const handleAssignmentSubmit = (submission: {
    checkpoint: string;
    files: FileItem[];
    comments?: string;
  }) => {
    handleCloseSubmitDialog();
  };

  const handleBack = (sess: string) => {
    const decodedSess = decodeURIComponent(sess);
    const isReviewSandbox = !isStudent && decodedSess.startsWith("reviews/");

    if (isReviewSandbox) {
      // Return to the Submission Review page the instructor came from
      navigate(-1);
      return;
    }

    const fromDetails = location.state?.fromDetails;

    if (isStudent) {
      if (isPracticeProject) {
        if (fromDetails) {
          navigate(-1);
        } else {
          navigate(paths.toPracticeProjectDetails(sess), { replace: true });
        }
      } else {
        if (fromDetails) {
          navigate(-1);
        } else {
          navigate(paths.toProjectDetails(sess), {
            state: { defaultTab: "checkpoints" },
            replace: true,
          });
        }
      }
    } else {
      if (isPracticeProject) {
        if (fromDetails) {
          navigate(-1);
        } else {
          navigate(paths.toPracticeProjectDetails(sess), { replace: true });
        }
      } else {
        if (fromDetails) {
          navigate(-1);
        } else {
          navigate(paths.instructorProjectDetails.replace(":projectId", sess), {
            replace: true,
          });
        }
      }
    }
  };

  useEffect(() => {
    const sid = workspace?.sessionId ?? null;
    if (!sessionLostToastReadyRef.current) {
      sessionLostToastReadyRef.current = true;
      prevWorkspaceSessionIdRef.current = sid;
      return;
    }
    const had = !!prevWorkspaceSessionIdRef.current;
    if (had && !sid) {
      toast.error(
        "Your sandbox session ended. Save your work, then refresh the page or reopen the notebook from the project.",
      );
    }
    prevWorkspaceSessionIdRef.current = sid;
  }, [workspace?.sessionId]);

  useEffect(() => {
    setSidebarVisible(false);
    return () => {
      setSidebarVisible(true);
    };
  }, [setSidebarVisible]);

  if (authFailed) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-red-500 mb-2">
            Connection Failed
          </h3>
          <p className="text-gray-600">
            Could not connect to the Jupyter server. Please ensure it is running
            and refresh.
          </p>
        </div>
      </div>
    );
  }

  const handleViewChange = (view: "explorer" | "messages") => {
    if (secondColumnView === view && sidebarWidth > 0) {
      // If already on this view, collapse it
      setSidebarWidth(0);
    } else {
      setSecondColumnView(view);
      if (sidebarWidth === 0) setSidebarWidth(320);
    }
  };

  return (
    <div className={cn("flex h-screen w-full overflow-hidden flex-col py-4 transition-colors duration-200", isDarkMode ? "dark bg-[#0d1117]" : "bg-white")}>
      <div className={cn("flex-1 flex flex-col overflow-hidden", isDarkMode && "sandbox-container")}>
        {/* Top Action Bar */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                handleBack(projectId!);
              }}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#30363d] transition-colors"
              title="Back to Projects"
            >
              <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-[#c9d1d9]" />
            </button>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-[#f0f6fc]">
                {activeProject?.title || "Jupyter Notebook Environment"}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Timer isDarkMode={isDarkMode} />
            
            <Button
              variant="outline"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full border-gray-200 dark:border-[#30363d] dark:bg-[#161b22] dark:text-gray-400 hover:dark:text-[#f0883e]"
            >
              {isDarkMode ? <Sun className="h-[1.2rem] w-[1.2rem]" /> : <Moon className="h-[1.2rem] w-[1.2rem]" />}
            </Button>

            {workspace?.project?.isGroupProject && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFileSharingOpen(true)}
                className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
              >
                <Share2 className="h-4 w-4 mr-1.5" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveNow}
              disabled={!toolbarState.isDirty || toolbarState.isSaving}
              title="Save (Ctrl+S or ⌘S)"
              className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {toolbarState.isSaving ? "Saving..." : "Save"}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUpload}
              className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
            >
              <Upload className="h-4 w-4 mr-1.5" />
              Upload
            </Button>
            {isStudent && !isPracticeProject && (
              <Button 
                variant="bits" 
                size="sm" 
                onClick={handleOpenSubmitDialog}
                className="dark:bg-[#f0883e] dark:hover:bg-[#e0772d] dark:text-white transition-colors"
              >
                Submit Assignment
              </Button>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Column 1: Icon Sidebar */}
          <div className="w-16 h-full bg-gray-50 dark:bg-[#0d1117] border-r border-gray-200 dark:border-[#30363d] flex flex-col items-center py-6 gap-4 flex-shrink-0">
            <button
              onClick={() => handleViewChange("explorer")}
              className={cn(
                "p-2 rounded-lg transition-colors",
                secondColumnView === "explorer" && sidebarWidth > 0
                  ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-[#f0883e]"
                  : "hover:bg-gray-200 dark:hover:bg-[#21262d] text-gray-700 dark:text-[#8b949e]",
              )}
              title="Files"
            >
              <File className="h-5 w-5" />
            </button>
            {workspace?.project?.isGroupProject && (
              <button
                onClick={() => handleViewChange("messages")}
                className={cn(
                  "p-2 rounded-lg transition-colors",
                  secondColumnView === "messages" && sidebarWidth > 0
                    ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-[#f0883e]"
                    : "hover:bg-gray-200 dark:hover:bg-[#21262d] text-gray-700 dark:text-[#8b949e]",
                )}
                title="Messages"
              >
                <MessageCircle className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => setIsAIChatOpen(!isAIChatOpen)}
              className={cn(
                "p-2 rounded-lg transition-colors",
                isAIChatOpen
                  ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-[#f0883e]"
                  : "hover:bg-gray-200 dark:hover:bg-[#21262d] text-gray-700 dark:text-[#8b949e]",
              )}
              title="AI Tutor"
            >
              <Bot className="h-5 w-5" />
            </button>
          </div>

          {/* Column 2: Project Explorer or Messages */}
          {sidebarWidth > 0 && (
            <div 
              className="h-full border-r border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] flex flex-col flex-shrink-0 overflow-hidden relative"
              style={{ width: `${sidebarWidth}px` }}
            >
              {secondColumnView === "explorer" ? (
                <Workspace
                  ref={workspaceRef}
                  projectId={projectId}
                  initialFile={(location.state as any)?.file}
                  onStateChange={handleWorkspaceStateChange}
                  onShare={() => setFileSharingOpen(true)}
                  onSave={handleSaveNow}
                  onUpload={handleUpload}
                  onSubmit={handleOpenSubmitDialog}
                  showOnlyExplorer={true}
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  isFileContentLoading={isFileContentLoading}
                  setIsFileContentLoading={setIsFileContentLoading}
                  pendingPath={pendingPath}
                  setPendingPath={setPendingPath}
                  isDarkMode={isDarkMode}
                  notebookDraftsRef={notebookDraftsRef}
                  stashNotebookBeforeNavigateRef={stashNotebookBeforeNavigateRef}
                />
              ) : (
                <ProjectMessagesChat projectId={projectId} isDarkMode={isDarkMode} />
              )}
            </div>
          )}

          {/* Resizer Handle */}
          <div
            className={cn(
              "w-1 hover:w-1.5 bg-transparent hover:bg-blue-400 dark:hover:bg-[#f0883e] cursor-col-resize flex-shrink-0 transition-all z-10",
              isResizing && "bg-blue-500 dark:bg-[#f0883e] w-1.5"
            )}
            onMouseDown={startResizing}
          />

          {/* Column 3: Main Workspace */}
          <div className="flex-1 h-full flex flex-col overflow-hidden bg-white dark:bg-[#0d1117]">
            <Workspace
              ref={workspaceRef}
              projectId={projectId}
              initialFile={(location.state as any)?.file}
              onStateChange={handleWorkspaceStateChange}
              onShare={() => setFileSharingOpen(true)}
              onSave={handleSaveNow}
              onUpload={handleUpload}
              onSubmit={handleOpenSubmitDialog}
              showOnlyContent={true}
              selectedFile={selectedFile}
              onFileSelect={handleFileSelect}
              isFileContentLoading={isFileContentLoading}
              setIsFileContentLoading={setIsFileContentLoading}
              pendingPath={pendingPath}
              setPendingPath={setPendingPath}
              isDarkMode={isDarkMode}
              notebookDraftsRef={notebookDraftsRef}
              stashNotebookBeforeNavigateRef={stashNotebookBeforeNavigateRef}
            />
          </div>

          {/* Resizer Handle (AI Sidebar) */}
          {isAIChatOpen && (
            <div
              className={cn(
                "w-1 hover:w-1.5 bg-transparent hover:bg-blue-400 dark:hover:bg-[#f0883e] cursor-col-resize flex-shrink-0 transition-all z-10",
                isAIResizing && "bg-blue-500 dark:bg-[#f0883e] w-1.5",
              )}
              onMouseDown={startAIResizing}
            />
          )}

          {/* Column 4: AI Sidebar */}
          {isAIChatOpen && (
            <div
              className="h-full bg-white dark:bg-[#161b22] flex flex-col flex-shrink-0 overflow-hidden"
              style={{ width: `${aiSidebarWidth}px` }}
            >
              <AIAssistantChat
                projectId={projectId!}
                isSidebar={true}
                onClose={() => setIsAIChatOpen(false)}
              />
            </div>
          )}
        </div>
      </div>

      <SubmitAssignmentDialog
        isOpen={isSubmitDialogOpen}
        onClose={handleCloseSubmitDialog}
        files={workspaceRef.current?.getFiles() || []}
        onAssignmentSubmit={handleAssignmentSubmit}
        project={activeProject}
        isDarkMode={isDarkMode}
      />

      <FileSharing
        open={isFileSharingOpen}
        onOpenChange={setFileSharingOpen}
        projectId={projectId}
        files={workspaceRef.current?.getFiles() || []}
        onShare={(data) => {
          toast.success("File shared successfully!");
        }}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
